import { useState, useEffect, useCallback, Fragment, useMemo, useRef } from "react"
import dayjs, { Dayjs } from "dayjs"
import styled from "styled-components"
import { message, Modal, DatePicker, TimePicker, Tooltip, Select, Button, Radio, Col, Row, Card, Space, Typography, Listy, Empty, Tag, Alert, Checkbox, Input, InputNumber, Divider, Segmented, Drawer, Switch, Table } from "antd"
import { PlusOutlined, EditOutlined, InfoCircleOutlined, LockOutlined } from "@ant-design/icons"
import { TiHomeOutline } from "react-icons/ti"
import { AiOutlineCloseCircle } from "react-icons/ai"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import {
  DistanceFormatter,
  gen,
  getActingUserRequestFields,
  resolveEventNumber,
  swimminglyApi,
} from "./utils/helperFunctions"
// Import directly (not the "./utils" barrel) to avoid a circular-import
// ordering issue that leaves these undefined under CJS/Jest.
import { CustomEventEmitter, IClub } from "./utils/sharedDataTypes"
import { toIsoStringUTC } from "./utils/TimeHelper"

import { cloneDeep } from "lodash"
import SingleRank from "./SingleRank"
import { updateSeasonMeetArrayListener } from "./MeetSchedulePageNew"
import { useFeatureAccess } from "./FeatureAccessContext"
import { FeatureKey } from "../constants/featureAccess"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faAngleLeft,
  faAngleRight,
  faMagnifyingGlass,
  faPeopleArrows,
  faPeoplePantsSimple,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons"
import PoolSelectionModal, { IPoolSelectionOption } from "./PoolSelectionModal"
import CreateMeetPoolModal from "./CreateMeetPoolModal"
import VirtualMeetInvitationResponseModal from "./VirtualMeetInvitationResponseModal"
import ImportEventOrderModal from "./ImportEventOrderModal"
import type { TMeetEvent } from "./ViewMeetLineUp"

interface IUpdateListener {
  on(event: "removeClub", listener: (clubId: number) => void): void
  emit(event: "removeClub", clubId: number): void
  removeAllListeners(event: "removeClub"): void
  on(event: "setHomeClub", listener: (clubId: number | null) => void): void
  emit(event: "setHomeClub", clubId: number | null): void
  removeAllListeners(event: "setHomeClub"): void
  on(event: "setPool", listener: (newPool: IMeetPool | null) => void): void
  emit(event: "setPool", newPool: IMeetPool | null): void
  removeAllListeners(event: "setPool"): void
  on(
    event: "updateSeasonForClubInMeet",
    listener: (
      clubId: number,
      newSeasonId: number,
      newSeasonName: string,
    ) => void,
  ): void
  emit(
    event: "updateSeasonForClubInMeet",
    clubId: number,
    newSeasonId: number,
    newSeasonName: string,
  ): void
  removeAllListeners(event: "updateSeasonForClubInMeet"): void
  on(
    event: "swapClubsInMeet",
    listener: (
      oldClubId: number,
      newClubId: number,
      seasonId: number | null,
      seasonName: string | null,
    ) => void,
  ): void
  emit(
    event: "swapClubsInMeet",
    oldClubId: number,
    newClubId: number,
    seasonId: number | null,
    seasonName: string | null,
  ): void
  removeAllListeners(event: "swapClubsInMeet"): void
  on(event: "addNextClub", listener: () => void): void
  emit(event: "addNextClub"): void
  removeAllListeners(event: "addNextClub"): void
  on(
    event: "updateIndividualScoringConfig",
    listener: (newSettings: { place: number; points: number }[]) => void,
  ): void
  emit(
    event: "updateIndividualScoringConfig",
    newSettings: { place: number; points: number }[],
  ): void
  removeAllListeners(event: "updateIndividualScoringConfig"): void
  on(
    event: "updateRelayScoringConfig",
    listener: (newSettings: { place: number; points: number }[]) => void,
  ): void
  emit(
    event: "updateRelayScoringConfig",
    newSettings: { place: number; points: number }[],
  ): void
  removeAllListeners(event: "updateRelayScoringConfig"): void
}

const componentUpdateListner: IUpdateListener = new CustomEventEmitter()

interface ICreateMeetModalProps {
  visible: boolean
  setVisible: (newVisible: boolean) => void
  refreshSchedule?: () => void
  season: {
    clubSeasonId: number
    seasonId: number
    seasonName: string
    startDate: Dayjs
    endDate: Dayjs
  } | null
}

interface IMeetPool {
  poolName: string
  poolId: number
  address: string | null
  length: number | null
  course: "SCY" | "SCM" | "LCM" | null
  clubId: number | null
  clubName: string | null
  description: string | null
  lat: number | null
  lng: number | null
  unitOfMeasure: number | null
  numLanes: number | null
  primaryPool: boolean
}

const StyledMeetModal = styled(Modal)`
  width: min(980px, calc(100vw - 32px)) !important;
  max-width: calc(100vw - 32px);
  min-width: min(725px, calc(100vw - 32px));

  .ant-modal-content {
    display: flex;
    flex-direction: column;
    max-height: calc(95vh - 100px);
    overflow: hidden;
    overflow-x: hidden;
  }

  .ant-modal-body {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px;
  }

  .ant-modal-body > .edit-step-shell,
  .ant-modal-body > .review-step-shell {
    width: 100%;
    min-width: 0;
  }

  .ant-modal-footer {
    position: sticky;
    bottom: 0;
    z-index: 2;
    margin-top: 0;
    padding: 16px 24px 20px;
    padding-top: 16px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.92), #fff 28px);
    border-top: 1px solid rgba(16, 18, 66, 0.08);
  }

  .create-meet-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  .create-meet-footer--edit {
    justify-content: flex-end;
  }

  .create-meet-footer--review {
    justify-content: space-between;
  }

  .create-meet-footer-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .overview-info {
    display: grid;
    grid-template-columns: 100px 1fr;
    align-items: center;
    row-gap: 8px;
  }

  .field-info-trigger.ant-btn {
    position: absolute;
    top: 50%;
    right: 50px;
    transform: translateY(-50%);
    height: 20px;
    width: 20px;
    min-width: 20px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(16, 18, 66, 0.72);
  }

  .field-info-trigger.ant-btn:hover,
  .field-info-trigger.ant-btn:focus {
    color: #101242;
  }

  .overview-info.meet-type-container .choice {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 5px;
  }

  .entry-limits {
    display: grid;
    align-items: center;
    grid-template-columns: 100px 100px 1fr;
    row-gap: 5px;
  }

  .title {
    color: var(--primaryblue);
  }

  .entry-limits .title {
    grid-column: span 3;
  }

  input:disabled {
    color: var(--mediumgrey);
  }

  .two-columns {
    margin-top: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 25px;
  }

  .two-columns p {
    text-align: center;
  }

  .virtual-course {
    display: flex;
    align-items: center;
  }

  label {
    margin: 0;
  }

  input[type="checkbox"] {
    margin: 0;
  }

  select {
    padding: 0;
  }

  input,
  select {
    border: 1px solid var(--mediumgrey);
    border-radius: 3px;
    height: 35px;
  }

  .shadowed {
    box-shadow: 0 1px 2px 2px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 5px;
    padding: 5px;
  }

  .event-order-select-wrap {
    position: relative;
    width: 100%;
    min-width: 0;
  }

  .event-order-select {
    width: 100%;
    min-width: 0;
    padding-right: 88px;
    cursor: pointer;
  }

  .event-order-preview-trigger.ant-btn {
    position: absolute;
    top: 50%;
    right: 26px;
    transform: translateY(-50%);
    height: 24px;
    width: 24px;
    min-width: 24px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(16, 18, 66, 0.72);
    cursor: pointer;
  }

  .event-order-preview-trigger.ant-btn:hover,
  .event-order-preview-trigger.ant-btn:focus {
    color: #101242;
  }

  .choose-dates {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .date-pickers {
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }

  .overview-info input:disabled {
    color: var(--darkgrey);
  }

  .chosen,
  .not-chosen {
    text-align: center;
    border-radius: 2px;
  }
  .chosen {
    background-color: var(--buttonblue);
    color: var(--white);
    border: none;
  }
  .not-chosen {
    background-color: var(--white);
    color: var(--darkgrey);
    border: 1.5px solid var(--mediumgrey);
  }

  .form-select {
    padding: 0;
    max-height: 50px;
  }

  .edit-step-shell {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    min-width: 0;
  }

  .edit-step-hero {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px 22px;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(240, 247, 255, 0.96), rgba(250, 252, 255, 0.98));
    border: 1px solid rgba(27, 71, 146, 0.12);
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .edit-step-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    width: 100%;
    min-width: 0;
  }

  .edit-step-card {
    border-radius: 16px;
    box-shadow: 0 12px 28px rgba(16, 18, 66, 0.06);
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .virtual-clubs-summary-card {
    width: 100%;
    min-width: 0;
  }

  .virtual-clubs-summary-card .ant-card-head,
  .virtual-clubs-summary-card .ant-card-head-wrapper,
  .virtual-clubs-summary-card .ant-card-head-title,
  .virtual-clubs-summary-card .ant-card-extra,
  .virtual-clubs-summary-card .ant-space,
  .virtual-clubs-summary-card .ant-list,
  .virtual-clubs-summary-card .ant-list-item,
  .virtual-clubs-summary-card .ant-typography {
    min-width: 0;
  }

  .in-person-clubs-shell {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
  }

  .in-person-club-list-card {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
    border-radius: 14px;
    border: 1px solid rgba(16, 18, 66, 0.1);
    background: rgba(250, 252, 255, 0.96);
    min-width: 0;
  }

  .in-person-club-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .in-person-club-row {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) 36px;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .in-person-club-host-button.ant-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    width: 36px;
    height: 100%;
    padding: 0;
    color: rgba(16, 18, 66, 0.28);
  }

  .in-person-club-host-button.ant-btn .anticon,
  .in-person-club-host-button.ant-btn svg {
    font-size: 1.8rem;
  }

  .in-person-club-host-button.ant-btn.is-active {
    color: #1b4792;
  }

  .in-person-club-field {
    position: relative;
    min-width: 0;
  }

  .in-person-club-field.has-host-chip .edit-readonly-box,
  .in-person-club-field.has-host-chip .ant-select-selector,
  .in-person-club-field.has-chip .edit-readonly-box {
    padding-right: 88px;
  }

  .in-person-club-field.has-chip.has-host-chip .edit-readonly-box {
    padding-right: 174px;
  }

  .in-person-club-field-chip {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    margin: 0;
    pointer-events: none;
  }

  .in-person-club-field-chip.in-person-club-field-chip-secondary {
    right: 96px;
  }

  .in-person-club-mobile-chip-row {
    display: none;
  }

  .in-person-pool-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px;
    border-radius: 12px;
    background: rgba(244, 248, 255, 0.9);
    border: 1px solid rgba(27, 71, 146, 0.12);
  }

  .in-person-pool-panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .in-person-pool-panel-copy {
    min-width: 0;
  }

  .in-person-pool-details {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .in-person-pool-detail--full {
    grid-column: 1 / -1;
  }

  .in-person-add-club.ant-btn {
    margin-top: 4px;
  }

  .edit-step-card .ant-card-head {
    border-bottom-color: rgba(16, 18, 66, 0.08);
    padding: 0 20px;
    min-height: 60px;
  }

  .edit-step-card .ant-card-head-title {
    min-width: 0;
  }

  .edit-step-card .ant-card-body {
    padding: 20px;
  }

  .edit-step-card-header-input {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    padding: 14px 0;
  }

  .edit-step-card-header-input .ant-input {
    border-radius: 12px;
  }

  .edit-field-stack {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .edit-field-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: rgba(16, 18, 66, 0.66);
  }

  .edit-field-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .edit-field-label-row .edit-field-label {
    margin: 0;
  }

  .edit-field-label-info.ant-btn {
    display: none;
    width: 24px;
    min-width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    box-shadow: none;
    color: rgba(16, 18, 66, 0.72);
  }

  .edit-field-label-info.ant-btn:hover,
  .edit-field-label-info.ant-btn:focus {
    color: #101242;
    background: transparent;
  }

  .edit-field-stack .ant-input,
  .edit-field-stack .ant-input-affix-wrapper,
  .edit-field-stack .ant-select-selector,
  .edit-field-stack .ant-picker,
  .edit-field-stack .ant-input-number,
  .edit-field-stack .ant-btn,
  .edit-meet-type-toggle.ant-segmented,
  .edit-meet-type-toggle .ant-segmented-item {
    border-radius: 12px;
  }

  .edit-event-order-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 40px 40px;
    gap: 8px;
    align-items: center;
  }

  .edit-meet-type-row {
    display: grid;
    grid-template-columns: max-content 40px;
    gap: 10px;
    align-items: center;
    justify-content: flex-start;
  }

  .edit-event-order-row .ant-btn,
  .edit-meet-type-row .ant-btn {
    width: 40px;
    min-width: 40px;
    padding-inline: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .edit-meet-type-toggle {
    display: inline-block;
    width: auto;
    max-width: 100%;
  }

  .edit-meet-type-toggle.ant-segmented {
    display: inline-block;
    width: auto;
    max-width: 100%;
    padding: 4px;
    background: rgba(244, 248, 255, 0.96);
  }

  .edit-meet-type-toggle .ant-segmented-group {
    width: auto;
    display: inline-grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    gap: 6px;
  }

  .edit-meet-type-toggle .ant-segmented-item {
    min-height: 40px;
  }

  .edit-meet-type-toggle .ant-segmented-item-label {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .edit-meet-type-option {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 32px;
    text-align: center;
    white-space: normal;
    line-height: 1.25;
    font-weight: 600;
    padding: 0 8px;
  }

  .edit-meet-type-label-mobile {
    display: none;
  }

  .edit-date-grid,
  .edit-limit-grid,
  .edit-scoring-limit-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .edit-limit-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .edit-limit-item {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
    border-radius: 12px;
    background: rgba(248, 250, 255, 0.92);
    border: 1px solid rgba(16, 18, 66, 0.08);
  }

  .edit-limit-item .ant-checkbox-wrapper {
    color: rgba(16, 18, 66, 0.78);
  }

  .edit-scoring-panel {
    padding: 16px;
    border-radius: 12px;
    background: rgba(248, 250, 255, 0.92);
    border: 1px solid rgba(16, 18, 66, 0.08);
  }

  .edit-readonly-box {
    padding: 12px 14px;
    border-radius: 12px;
    background: rgba(248, 250, 255, 0.92);
    border: 1px solid rgba(16, 18, 66, 0.08);
    min-height: 44px;
    display: flex;
    align-items: center;
    color: #101242;
  }

  .edit-inline-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .edit-inline-actions--deadline {
    flex-wrap: nowrap;
  }

  .edit-section-note {
    margin: 0;
    color: rgba(16, 18, 66, 0.66);
  }

  .edit-step-meet-name-preview {
    margin: 0 0 8px;
    color: #101242;
    font-size: 22px;
    line-height: 1.2;
    font-weight: 600;
  }

  .edit-step-meet-name-preview.is-empty {
    color: rgba(16, 18, 66, 0.42);
    font-weight: 500;
  }

  .review-step-shell {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    min-width: 0;
  }

  .review-step-hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 22px;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(16, 18, 66, 0.96), rgba(27, 71, 146, 0.92));
    color: #fff;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .review-step-hero .ant-typography,
  .review-step-hero .ant-typography strong {
    color: #fff;
  }

  .review-step-hero-copy {
    flex: 1;
    min-width: 0;
  }

  .review-step-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    width: 100%;
    min-width: 0;
  }

  .review-step-card {
    border-radius: 16px;
    box-shadow: 0 12px 28px rgba(16, 18, 66, 0.08);
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .review-step-card.is-confirmed {
    border: 1px solid rgba(82, 196, 26, 0.35);
    box-shadow: 0 14px 30px rgba(82, 196, 26, 0.08);
  }

  .review-step-card.is-confirmed .ant-card-head {
    background: rgba(246, 255, 237, 0.9);
  }

  .review-step-card .ant-card-head {
    border-bottom-color: rgba(16, 18, 66, 0.08);
  }

  .review-step-card .ant-card-head-wrapper {
    align-items: flex-start;
    gap: 12px;
  }

  .review-step-card .ant-card-extra {
    margin-inline-start: auto;
    padding: 0;
  }

  .review-section-check {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: rgba(16, 18, 66, 0.82);
  }

  .review-section-check .ant-checkbox + span {
    padding-inline-start: 6px;
    padding-inline-end: 0;
  }

  .review-section-check-tag {
    margin-inline-end: 0;
  }

  .review-step-list .ant-list-item {
    padding-left: 0;
    padding-right: 0;
  }

  .review-step-list-label {
    color: rgba(16, 18, 66, 0.64);
    font-size: 12px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .review-step-list-value {
    text-align: right;
    color: #101242;
    font-weight: 600;
    padding-left: 12px;
  }

  .review-step-list-value.subtle {
    color: rgba(16, 18, 66, 0.62);
    font-weight: 500;
    font-style: italic;
  }

  .review-step-card .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .review-step-card .ant-checkbox {
    margin-top: 3px;
  }

  .review-step-card .ant-alert {
    border-radius: 12px;
  }

  @media (max-width: 750px) {
    width: calc(100vw - 12px) !important;
    max-width: calc(100vw - 12px);
    min-width: 0;

    .ant-modal {
      top: 6px;
      padding-bottom: 6px;
    }

    .ant-modal-content {
      max-height: calc(100vh - 12px);
      border-radius: 14px;
    }

    .ant-modal-header {
      padding: 14px 16px 10px;
    }

    .ant-modal-body {
      padding: 0;
    }

    .ant-modal-footer {
      padding: 12px;
    }

    .ant-modal-title {
      font-size: 15px;
      line-height: 1.35;
      padding-right: 24px;
    }

    .two-columns {
      grid-template-columns: 1fr;
    }

    .two-columns.no-mobile {
      grid-template-columns: 1fr 1fr;
    }

    .edit-date-grid,
    .edit-limit-grid,
    .edit-scoring-limit-grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .edit-step-shell,
    .edit-step-grid,
    .review-step-shell,
    .review-step-grid {
      gap: 12px;
    }

    .edit-step-hero,
    .review-step-hero {
      padding: 16px;
      border-radius: 14px;
    }

    .edit-step-meet-name-preview {
      font-size: 18px;
    }

    .edit-step-card,
    .review-step-card {
      border-radius: 14px;
    }

    .virtual-clubs-summary-card .ant-card-head {
      padding: 0 14px;
    }

    .virtual-clubs-summary-card .ant-card-head-wrapper {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
      padding: 12px 0;
    }

    .virtual-clubs-summary-card .ant-card-head-title {
      white-space: normal;
    }

    .virtual-clubs-summary-card .ant-card-extra {
      margin-inline-start: 0;
    }

    .virtual-clubs-summary-card .ant-card-extra .ant-btn {
      width: 100%;
    }

    .virtual-clubs-summary-card .ant-list-item {
      padding-left: 12px;
      padding-right: 12px;
    }

    .in-person-club-list-card {
      padding: 14px;
      border-radius: 12px;
    }

    .in-person-club-list-header,
    .in-person-pool-panel-header,
    .in-person-club-list-header-copy {
      flex-direction: column;
      align-items: stretch;
    }

    .in-person-club-list-header {
      flex-direction: column;
      align-items: stretch;
    }

    .in-person-club-row {
      grid-template-columns: 32px minmax(0, 1fr) 32px;
      gap: 8px;
    }

    .in-person-club-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .in-person-club-field.has-host-chip .edit-readonly-box,
    .in-person-club-field.has-host-chip .ant-select-selector,
    .in-person-club-field.has-chip .edit-readonly-box,
    .in-person-club-field.has-chip.has-host-chip .edit-readonly-box {
      padding-right: 14px;
    }

    .in-person-club-field-chip {
      display: none;
    }

    .in-person-club-mobile-chip-row {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .in-person-club-mobile-chip-row .ant-tag {
      margin: 0;
    }

    .in-person-pool-panel-header .ant-btn,
    .in-person-club-list-header .ant-btn {
      width: 100%;
    }

    .in-person-pool-details {
      grid-template-columns: 1fr;
    }

    .in-person-pool-detail--full {
      grid-column: auto;
    }

    .edit-step-card .ant-card-head,
    .review-step-card .ant-card-head {
      padding: 0 14px;
      min-height: 54px;
    }

    .edit-step-card-header-input {
      padding: 12px 0;
    }

    .edit-step-card .ant-card-body,
    .review-step-card .ant-card-body {
      padding: 14px;
    }

    .edit-event-order-row {
      grid-template-columns: minmax(0, 1fr) 40px 40px;
      align-items: stretch;
    }

    .edit-meet-type-row {
      grid-template-columns: max-content 40px;
      align-items: center;
    }

    .core-setup-card .edit-event-order-row {
      width: 100%;
      grid-template-columns: minmax(0, 1fr) 40px;
      gap: 6px;
    }

    .core-setup-card .edit-field-label-row .edit-field-label-info.ant-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .core-setup-card .edit-event-order-row .edit-event-order-info-button,
    .core-setup-card .edit-meet-type-row .edit-meet-type-info-button {
      display: none;
    }

    .core-setup-card .edit-event-order-row .ant-select {
      min-width: 0;
    }

    .core-setup-card .edit-meet-type-row {
      width: 100%;
      grid-template-columns: minmax(0, 1fr);
      align-items: stretch;
    }

    .core-setup-card .edit-meet-type-toggle,
    .core-setup-card .edit-meet-type-toggle.ant-segmented {
      display: block;
      width: 100%;
    }

    .core-setup-card .edit-meet-type-toggle .ant-segmented-group {
      width: 100%;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-auto-flow: initial;
      grid-auto-columns: auto;
    }

    .core-setup-card .edit-meet-type-toggle .ant-segmented-item-label {
      min-width: 0;
    }

    .edit-meet-type-option {
      justify-content: flex-start;
      text-align: left;
      min-height: 42px;
      padding: 0 8px;
    }

    .core-setup-card .edit-meet-type-option {
      justify-content: center;
      text-align: center;
      padding: 0 6px;
      gap: 6px;
    }

    .edit-meet-type-label-desktop {
      display: none;
    }

    .edit-meet-type-label-mobile {
      display: inline;
    }

    .edit-inline-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .edit-inline-actions .ant-btn {
      width: 100%;
    }

    .edit-inline-actions--deadline {
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      flex-wrap: nowrap;
    }

    .edit-inline-actions--deadline .ant-btn {
      width: auto;
      flex: 0 0 auto;
    }

    .review-step-hero {
      flex-direction: column;
    }

    .review-step-grid {
      grid-template-columns: 1fr;
    }

    .create-meet-footer,
    .create-meet-footer-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .create-meet-footer .ant-btn,
    .create-meet-footer-actions .ant-btn {
      width: 100%;
    }
  }

  /* new rules to make Radio.Buttons share full width and allow multi-word labels */
  .overview-info.meet-type-container .ant-radio-group {
    display: flex;
    width: 100%;
    min-width: 0;
  }
  .overview-info.meet-type-container .ant-radio-button-wrapper {
    flex: 1;
    text-align: center;
    white-space: normal; /* allow wrapping if needed */
    display: flex;
    align-items: stretch;
    justify-content: center;
    min-width: 0;
    height: auto;
    line-height: 1.3;
  }
  .overview-info.meet-type-container .ant-radio-button-wrapper > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    white-space: normal;
    flex-wrap: wrap;
    text-align: center;
    padding: 8px 10px;
    min-width: 0;
  }

  @media (max-width: 750px) {
    .overview-info.meet-type-container .ant-radio-group {
      flex-direction: column;
      gap: 8px;
    }

    .overview-info.meet-type-container .ant-radio-button-wrapper {
      width: 100%;
      margin-left: 0 !important;
      border-radius: 6px !important;
      border-left-width: 1px !important;
    }
  }
`

const StyledMeetDrawer = styled(Drawer)`
  .ant-drawer-content {
    height: 100dvh;
    border-radius: 22px 22px 0 0;
    overflow: hidden;
  }

  .ant-drawer-header {
    padding: 14px 16px 10px;
    background: #fff;
    border-bottom: 1px solid rgba(16, 18, 66, 0.08);
  }

  .ant-drawer-title {
    font-size: 15px;
    line-height: 1.35;
    padding-right: 24px;
  }

  .ant-drawer-body {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px 20px 24px;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .ant-drawer-body > .edit-step-shell,
  .ant-drawer-body > .review-step-shell {
    width: 100%;
    min-width: 0;
  }

  .create-meet-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  .create-meet-footer--edit {
    justify-content: flex-end;
  }

  .create-meet-footer--review {
    justify-content: space-between;
  }

  .create-meet-footer-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .edit-step-shell {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    min-width: 0;
  }

  .edit-step-hero {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px 22px;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(240, 247, 255, 0.96), rgba(250, 252, 255, 0.98));
    border: 1px solid rgba(27, 71, 146, 0.12);
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .edit-step-grid,
  .review-step-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    width: 100%;
    min-width: 0;
  }

  .edit-step-card,
  .review-step-card {
    border-radius: 16px;
    box-shadow: 0 12px 28px rgba(16, 18, 66, 0.06);
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .edit-step-card .ant-card-head {
    border-bottom-color: rgba(16, 18, 66, 0.08);
    padding: 0 20px;
    min-height: 60px;
  }

  .edit-step-card .ant-card-head-title,
  .review-step-card .ant-card-head-title {
    min-width: 0;
  }

  .edit-step-card .ant-card-body,
  .review-step-card .ant-card-body {
    padding: 20px;
  }

  .edit-step-card-header-input {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    padding: 14px 0;
  }

  .edit-step-card-header-input .ant-input {
    border-radius: 12px;
  }

  .edit-field-stack {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .edit-field-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: rgba(16, 18, 66, 0.66);
  }

  .edit-field-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .edit-field-label-row .edit-field-label {
    margin: 0;
  }

  .edit-field-label-info.ant-btn {
    display: none;
    width: 24px;
    min-width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    box-shadow: none;
    color: rgba(16, 18, 66, 0.72);
  }

  .edit-field-label-info.ant-btn:hover,
  .edit-field-label-info.ant-btn:focus {
    color: #101242;
    background: transparent;
  }

  .edit-field-stack .ant-input,
  .edit-field-stack .ant-input-affix-wrapper,
  .edit-field-stack .ant-select-selector,
  .edit-field-stack .ant-picker,
  .edit-field-stack .ant-input-number,
  .edit-field-stack .ant-btn,
  .edit-meet-type-toggle.ant-segmented,
  .edit-meet-type-toggle .ant-segmented-item {
    border-radius: 12px;
  }

  .edit-event-order-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 40px 40px;
    gap: 8px;
    align-items: center;
  }

  .edit-meet-type-row {
    display: grid;
    grid-template-columns: max-content 40px;
    gap: 10px;
    align-items: center;
    justify-content: flex-start;
  }

  .edit-event-order-row .ant-btn,
  .edit-meet-type-row .ant-btn {
    width: 40px;
    min-width: 40px;
    padding-inline: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .edit-meet-type-toggle {
    display: inline-block;
    width: auto;
    max-width: 100%;
  }

  .edit-meet-type-toggle.ant-segmented {
    display: inline-block;
    width: auto;
    max-width: 100%;
    padding: 4px;
    background: rgba(244, 248, 255, 0.96);
  }

  .edit-meet-type-toggle .ant-segmented-group {
    width: auto;
    display: inline-grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    gap: 6px;
  }

  .edit-meet-type-toggle .ant-segmented-item {
    min-height: 40px;
  }

  .edit-meet-type-toggle .ant-segmented-item-label {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .edit-meet-type-option {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 32px;
    text-align: center;
    white-space: normal;
    line-height: 1.25;
    font-weight: 600;
    padding: 0 8px;
  }

  .edit-meet-type-label-mobile {
    display: none;
  }

  .edit-date-grid,
  .edit-scoring-limit-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  .edit-limit-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }

  .edit-limit-item {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
    border-radius: 12px;
    background: rgba(248, 250, 255, 0.92);
    border: 1px solid rgba(16, 18, 66, 0.08);
  }

  .edit-limit-item .ant-checkbox-wrapper {
    color: rgba(16, 18, 66, 0.78);
  }

  .edit-scoring-panel {
    padding: 16px;
    border-radius: 12px;
    background: rgba(248, 250, 255, 0.92);
    border: 1px solid rgba(16, 18, 66, 0.08);
  }

  .edit-readonly-box {
    padding: 12px 14px;
    border-radius: 12px;
    background: rgba(248, 250, 255, 0.92);
    border: 1px solid rgba(16, 18, 66, 0.08);
    min-height: 44px;
    display: flex;
    align-items: center;
    color: #101242;
  }

  .edit-inline-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .edit-inline-actions--deadline {
    flex-wrap: nowrap;
  }

  .edit-section-note {
    margin: 0;
    color: rgba(16, 18, 66, 0.66);
  }

  .edit-step-meet-name-preview {
    margin: 0 0 8px;
    color: #101242;
    font-size: 20px;
    line-height: 1.2;
    font-weight: 600;
  }

  .edit-step-meet-name-preview.is-empty {
    color: rgba(16, 18, 66, 0.42);
    font-weight: 500;
  }

  .virtual-clubs-summary-card {
    width: 100%;
    min-width: 0;
  }

  .virtual-clubs-summary-card .ant-card-head,
  .virtual-clubs-summary-card .ant-card-head-wrapper,
  .virtual-clubs-summary-card .ant-card-head-title,
  .virtual-clubs-summary-card .ant-card-extra,
  .virtual-clubs-summary-card .ant-space,
  .virtual-clubs-summary-card .ant-list,
  .virtual-clubs-summary-card .ant-list-item,
  .virtual-clubs-summary-card .ant-typography {
    min-width: 0;
  }

  .in-person-clubs-shell {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
  }

  .in-person-club-list-card {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px;
    border-radius: 14px;
    border: 1px solid rgba(16, 18, 66, 0.1);
    background: rgba(250, 252, 255, 0.96);
    min-width: 0;
  }

  .in-person-club-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .in-person-club-row {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) 36px;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .in-person-club-host-button.ant-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    width: 36px;
    height: 100%;
    padding: 0;
    color: rgba(16, 18, 66, 0.28);
  }

  .in-person-club-host-button.ant-btn .anticon,
  .in-person-club-host-button.ant-btn svg {
    font-size: 1.8rem;
  }

  .in-person-club-host-button.ant-btn.is-active {
    color: #1b4792;
  }

  .in-person-club-field {
    position: relative;
    min-width: 0;
  }

  .in-person-club-field.has-host-chip .edit-readonly-box,
  .in-person-club-field.has-host-chip .ant-select-selector,
  .in-person-club-field.has-chip .edit-readonly-box {
    padding-right: 88px;
  }

  .in-person-club-field.has-chip.has-host-chip .edit-readonly-box {
    padding-right: 174px;
  }

  .in-person-club-field-chip {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    margin: 0;
    pointer-events: none;
  }

  .in-person-club-field-chip.in-person-club-field-chip-secondary {
    right: 96px;
  }

  .in-person-club-mobile-chip-row {
    display: none;
  }

  .in-person-pool-panel {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px;
    border-radius: 12px;
    background: rgba(244, 248, 255, 0.9);
    border: 1px solid rgba(27, 71, 146, 0.12);
  }

  .in-person-pool-panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .in-person-pool-panel-copy {
    min-width: 0;
  }

  .in-person-pool-details {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .in-person-pool-detail--full {
    grid-column: 1 / -1;
  }

  .in-person-add-club.ant-btn {
    margin-top: 4px;
  }

  .review-step-shell {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    min-width: 0;
  }

  .review-step-hero {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 22px;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(16, 18, 66, 0.96), rgba(27, 71, 146, 0.92));
    color: #fff;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .review-step-hero .ant-typography,
  .review-step-hero .ant-typography strong {
    color: #fff;
  }

  .review-step-hero-copy {
    flex: 1;
    min-width: 0;
  }

  .review-step-card.is-confirmed {
    border: 1px solid rgba(82, 196, 26, 0.35);
    box-shadow: 0 14px 30px rgba(82, 196, 26, 0.08);
  }

  .review-step-card.is-confirmed .ant-card-head {
    background: rgba(246, 255, 237, 0.9);
  }

  .review-step-card .ant-card-head {
    border-bottom-color: rgba(16, 18, 66, 0.08);
  }

  .review-step-card .ant-card-head-wrapper {
    align-items: flex-start;
    gap: 12px;
  }

  .review-step-card .ant-card-extra {
    margin-inline-start: auto;
    padding: 0;
  }

  .review-section-check {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: rgba(16, 18, 66, 0.82);
  }

  .review-section-check .ant-checkbox + span {
    padding-inline-start: 6px;
    padding-inline-end: 0;
  }

  .review-section-check-tag {
    margin-inline-end: 0;
  }

  .review-step-list .ant-list-item {
    padding-left: 0;
    padding-right: 0;
  }

  .review-step-list-label {
    color: rgba(16, 18, 66, 0.64);
    font-size: 12px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .review-step-list-value {
    text-align: right;
    color: #101242;
    font-weight: 600;
    padding-left: 12px;
  }

  .review-step-list-value.subtle {
    color: rgba(16, 18, 66, 0.62);
    font-weight: 500;
    font-style: italic;
  }

  .review-step-card .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .review-step-card .ant-checkbox {
    margin-top: 3px;
  }

  .review-step-card .ant-alert {
    border-radius: 12px;
  }

  @media (max-width: 750px) {
    .edit-date-grid,
    .edit-limit-grid,
    .edit-scoring-limit-grid {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    .edit-step-shell,
    .edit-step-grid,
    .review-step-shell,
    .review-step-grid {
      gap: 12px;
    }

    .edit-step-hero,
    .review-step-hero {
      padding: 16px;
      border-radius: 14px;
    }

    .edit-step-meet-name-preview {
      font-size: 18px;
    }

    .edit-step-card,
    .review-step-card {
      border-radius: 14px;
    }

    .virtual-clubs-summary-card .ant-card-head {
      padding: 0 14px;
    }

    .virtual-clubs-summary-card .ant-card-head-wrapper {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
      padding: 12px 0;
    }

    .virtual-clubs-summary-card .ant-card-head-title {
      white-space: normal;
    }

    .virtual-clubs-summary-card .ant-card-extra {
      margin-inline-start: 0;
    }

    .virtual-clubs-summary-card .ant-card-extra .ant-btn {
      width: 100%;
    }

    .virtual-clubs-summary-card .ant-list-item {
      padding-left: 12px;
      padding-right: 12px;
    }

    .in-person-club-list-card {
      padding: 14px;
      border-radius: 12px;
    }

    .in-person-club-list-header,
    .in-person-pool-panel-header,
    .in-person-club-list-header-copy {
      flex-direction: column;
      align-items: stretch;
    }

    .in-person-club-row {
      grid-template-columns: 32px minmax(0, 1fr) 32px;
      gap: 8px;
    }

    .in-person-club-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .in-person-club-field.has-host-chip .edit-readonly-box,
    .in-person-club-field.has-host-chip .ant-select-selector,
    .in-person-club-field.has-chip .edit-readonly-box,
    .in-person-club-field.has-chip.has-host-chip .edit-readonly-box {
      padding-right: 14px;
    }

    .in-person-club-field-chip {
      display: none;
    }

    .in-person-club-mobile-chip-row {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .in-person-club-mobile-chip-row .ant-tag {
      margin: 0;
    }

    .in-person-pool-panel-header .ant-btn,
    .in-person-club-list-header .ant-btn {
      width: 100%;
    }

    .in-person-pool-details {
      grid-template-columns: 1fr;
    }

    .in-person-pool-detail--full {
      grid-column: auto;
    }

    .edit-step-card .ant-card-head,
    .review-step-card .ant-card-head {
      padding: 0 14px;
      min-height: 54px;
    }

    .edit-step-card-header-input {
      padding: 12px 0;
    }

    .edit-step-card .ant-card-body,
    .review-step-card .ant-card-body {
      padding: 14px;
    }

    .edit-event-order-row {
      grid-template-columns: minmax(0, 1fr) 40px 40px;
      align-items: stretch;
    }

    .edit-meet-type-row {
      grid-template-columns: max-content 40px;
      align-items: center;
    }

    .core-setup-card .edit-event-order-row {
      width: 100%;
      grid-template-columns: minmax(0, 1fr) 40px;
      gap: 6px;
    }

    .core-setup-card .edit-field-label-row .edit-field-label-info.ant-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .core-setup-card .edit-event-order-row .edit-event-order-info-button,
    .core-setup-card .edit-meet-type-row .edit-meet-type-info-button {
      display: none;
    }

    .core-setup-card .edit-event-order-row .ant-select {
      min-width: 0;
    }

    .core-setup-card .edit-meet-type-row {
      width: 100%;
      grid-template-columns: minmax(0, 1fr);
      align-items: stretch;
    }

    .core-setup-card .edit-meet-type-toggle,
    .core-setup-card .edit-meet-type-toggle.ant-segmented {
      display: block;
      width: 100%;
    }

    .core-setup-card .edit-meet-type-toggle .ant-segmented-group {
      width: 100%;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-auto-flow: initial;
      grid-auto-columns: auto;
    }

    .core-setup-card .edit-meet-type-toggle .ant-segmented-item-label {
      min-width: 0;
    }

    .edit-meet-type-option {
      justify-content: flex-start;
      text-align: left;
      min-height: 42px;
      padding: 0 8px;
    }

    .core-setup-card .edit-meet-type-option {
      justify-content: center;
      text-align: center;
      padding: 0 6px;
      gap: 6px;
    }

    .edit-meet-type-label-desktop {
      display: none;
    }

    .edit-meet-type-label-mobile {
      display: inline;
    }

    .edit-inline-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .edit-inline-actions .ant-btn {
      width: 100%;
    }

    .edit-inline-actions--deadline {
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      flex-wrap: nowrap;
    }

    .edit-inline-actions--deadline .ant-btn {
      width: auto;
      flex: 0 0 auto;
    }

    .review-step-hero {
      flex-direction: column;
    }
  }

  .ant-drawer-footer {
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), #fff 24px);
    border-top: 1px solid rgba(16, 18, 66, 0.08);
    box-shadow: 0 -10px 24px rgba(16, 18, 66, 0.08);
  }

  .create-meet-footer,
  .create-meet-footer-actions {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 10px;
  }

  .create-meet-footer .ant-btn,
  .create-meet-footer-actions .ant-btn {
    width: 100%;
  }
`

const { Text } = Typography

const getClubDisplayName = (club: IClub) =>
  club.nameLong || club.name_long || club.name || `Club ${club.clubId || ""}`

const getClubSearchLabel = (club: IClub) =>
  [
    getClubDisplayName(club),
    club.name,
    club.leagueName,
    club.organizationNames,
  ]
    .filter(Boolean)
    .join(" ")

const getOrdinalLabel = (value: number) => {
  const remainder10 = value % 10
  const remainder100 = value % 100

  if (remainder10 === 1 && remainder100 !== 11) return `${value}st`
  if (remainder10 === 2 && remainder100 !== 12) return `${value}nd`
  if (remainder10 === 3 && remainder100 !== 13) return `${value}rd`
  return `${value}th`
}

const formatScoringSettings = (
  settings: { place: number; points: number }[],
) => settings.map((setting) => `${getOrdinalLabel(setting.place)}: ${setting.points} pts`).join(", ")

interface IVirtualOrganizationGroup {
  organizationId: number
  organizationName: string
  clubs: IClub[]
}

type ChampionshipSessionType = "timedFinals" | "prelims" | "finals"

interface ChampionshipSession {
  id: string
  type: ChampionshipSessionType
  date: Dayjs | null
  time: Dayjs | null
  entryLimitsEnabled: boolean
  hasIndividualEntryLimit: boolean
  individualEntryLimit: number
  hasRelayEntryLimit: boolean
  relayEntryLimit: number
  hasTotalEntryLimit: boolean
  totalEntryLimit: number | null
  sessionNumber: number | null
}

interface IReviewSectionsState {
  meetDetails: boolean
  eventOrder: boolean
  participatingClubs: boolean
  entryLimits: boolean
  championshipConfig: boolean
}

const DEFAULT_REVIEW_SECTIONS_STATE: IReviewSectionsState = {
  meetDetails: false,
  eventOrder: false,
  participatingClubs: false,
  entryLimits: false,
  championshipConfig: false,
}

const SESSION_COLORS_LIST = ["#1677ff", "#52c41a", "#fa8c16", "#722ed1", "#eb2f96", "#13c2c2"]

function IndividualScoringConfig({
  individualScoring,
}: {
  individualScoring: { place: number; points: number }[]
}) {
  const changePlace = (
    index: number,
    place: number | null,
    points: number | null,
  ) => {
    const newIndividualScoring = cloneDeep(individualScoring)
    const updateItem = newIndividualScoring[index]
    if (place) updateItem.place = place
    if (points) updateItem.points = points
    componentUpdateListner.emit(
      "updateIndividualScoringConfig",
      newIndividualScoring,
    )
  }

  const removeIndex = (index: number) => {
    const newIndividualScoring = cloneDeep(individualScoring)
    newIndividualScoring.splice(index, 1)
    componentUpdateListner.emit(
      "updateIndividualScoringConfig",
      newIndividualScoring,
    )
  }

  const add = () => {
    const newIndividualScoring = cloneDeep(individualScoring)
    const maxPlace = Math.max(...newIndividualScoring.map((el) => el.place), 0)
    newIndividualScoring.push({ place: maxPlace + 1, points: 1 })
    componentUpdateListner.emit(
      "updateIndividualScoringConfig",
      newIndividualScoring,
    )
  }

  return (
    <div style={{ display: "flex", justifyContent: "space-around" }}>
      <div>
        <div style={{ textAlign: "center", fontSize: "20px" }}>Individual</div>
        <div style={{ display: "flex" }}>
          <div style={{ width: "20%" }} />
          <div style={{ width: "100px" }}>Place</div>
          <div style={{ width: "100px" }}>Points</div>
        </div>
        {individualScoring.map((el, i) => (
          <SingleRank
            key={`individual_scoring_${i}`}
            place={el.place}
            points={el.points}
            changePlace={(place) => changePlace(i, place, null)}
            changePoints={(points) => changePlace(i, null, points)}
            remove={() => removeIndex(i)}
          />
        ))}
        <Button
          onClick={add}
          shape="circle"
          icon={<PlusOutlined />}
          style={{ marginTop: "5px" }}
        />
      </div>
    </div>
  )
}

function RelayScoringConfig({
  relayScoring,
}: {
  relayScoring: { place: number; points: number }[]
}) {
  const changePlace = (
    index: number,
    place: number | null,
    points: number | null,
  ) => {
    const newRelayScoring = cloneDeep(relayScoring)
    const updateItem = newRelayScoring[index]
    if (place) updateItem.place = place
    if (points) updateItem.points = points
    componentUpdateListner.emit("updateRelayScoringConfig", newRelayScoring)
  }

  const removeIndex = (index: number) => {
    const newRelayScoring = cloneDeep(relayScoring)
    newRelayScoring.splice(index, 1)
    componentUpdateListner.emit("updateRelayScoringConfig", newRelayScoring)
  }

  const add = () => {
    const newRelayScoring = cloneDeep(relayScoring)
    const maxPlace = Math.max(...newRelayScoring.map((el) => el.place), 0)
    newRelayScoring.push({ place: maxPlace + 1, points: 1 })
    componentUpdateListner.emit("updateRelayScoringConfig", newRelayScoring)
  }

  return (
    <div style={{ display: "flex", justifyContent: "space-around" }}>
      <div>
        <div style={{ textAlign: "center", fontSize: "20px" }}>Relay</div>
        <div style={{ display: "flex" }}>
          <div style={{ width: "20%" }} />
          <div style={{ width: "100px" }}>Place</div>
          <div style={{ width: "100px" }}>Points</div>
        </div>
        {relayScoring.map((el, i) => (
          <SingleRank
            key={`relay_scoring_${i}`}
            place={el.place}
            points={el.points}
            changePlace={(place) => changePlace(i, place, null)}
            changePoints={(points) => changePlace(i, null, points)}
            remove={() => removeIndex(i)}
          />
        ))}
        <Button
          onClick={add}
          shape="circle"
          icon={<PlusOutlined />}
          style={{ marginTop: "5px" }}
        />
      </div>
    </div>
  )
}

function VirtualClubRow({
  club,
  clubSeasonsInMeet,
  availableClubs,
  leagueClubs,
  organizationGroups,
  onSwapClub,
  onRemoveClub,
}: {
  club: IClub
  clubSeasonsInMeet: {
    club: IClub
    season: { seasonId: number; seasonName: string } | null
  }[]
  availableClubs: IClub[]
  leagueClubs: IClub[]
  organizationGroups: IVirtualOrganizationGroup[]
  onSwapClub: (
    oldClubId: number,
    newClubId: number,
    seasonId: number | null,
    seasonName: string | null,
  ) => void
  onRemoveClub: (clubId: number) => void
}) {
  const [clubSearchValue, setClubSearchValue] = useState("")

  const selectedClubId = club.clubId && club.clubId > 0 ? club.clubId : undefined

  const clubOptions = useMemo(() => {
    const selectedIds = new Set(
      clubSeasonsInMeet
        .map((clubSeason) => clubSeason.club.clubId)
        .filter((clubId): clubId is number => typeof clubId === "number" && clubId > 0),
    )

    if (selectedClubId) {
      selectedIds.delete(selectedClubId)
    }

    const filteredClubs = availableClubs.filter(
      (clubOption) => !selectedIds.has(clubOption.clubId || -1),
    )
    const leagueClubIds = new Set(
      leagueClubs
        .map((clubOption) => clubOption.clubId)
        .filter((clubId): clubId is number => typeof clubId === "number"),
    )
    const groupedOrganizationClubs = organizationGroups.map((organization) => ({
      organizationId: organization.organizationId,
      organizationName: organization.organizationName,
      clubs: filteredClubs.filter((clubOption) =>
        organization.clubs.some(
          (organizationClub) => organizationClub.clubId === clubOption.clubId,
        ),
      ),
    }))

    const sameLeague: IClub[] = []
    const others: IClub[] = []

    filteredClubs.forEach((clubOption) => {
      const clubId = clubOption.clubId
      const isSameLeague = typeof clubId === "number" && leagueClubIds.has(clubId)

      if (isSameLeague) {
        sameLeague.push(clubOption)
        return
      }

      others.push(clubOption)
    })

    const normalizedSearch = clubSearchValue.trim().toLowerCase()
    if (normalizedSearch) {
      return filteredClubs.filter((clubOption) =>
        getClubSearchLabel(clubOption).toLowerCase().includes(normalizedSearch),
      )
    }

    return { sameLeague, organizationGroups: groupedOrganizationClubs, others }
  }, [
    availableClubs,
    clubSeasonsInMeet,
    selectedClubId,
    leagueClubs,
    organizationGroups,
    clubSearchValue,
  ])

  const renderClubOption = (clubOption: IClub) => (
    <Select.Option
      key={`available_club_virtual_choice_${club.clubId || -1}_${clubOption.clubId}`}
      value={clubOption.clubId || -1}
    >
      {getClubDisplayName(clubOption)}
    </Select.Option>
  )

  const leagueGroupLabel =
    leagueClubs.find((clubOption) => clubOption.leagueName)?.leagueName ||
    leagueClubs.find((clubOption) => clubOption.league)?.league ||
    "Clubs In Your League"

  return (
    <Card
      size="small"
      style={{ borderRadius: 12 }}
      styles={{ body: { padding: 10 } }}
    >
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} md={10}>
          <Select
            value={selectedClubId}
            size="middle"
            style={{ width: "100%" }}
            placeholder="Choose Club"
            onOpenChange={(open) => {
              if (!open) {
                setClubSearchValue("")
              }
            }}
            onChange={(newClubId) => {
              onSwapClub(
                club.clubId || -1,
                newClubId,
                null,
                null,
              )
            }}
            showSearch={{
              filterOption: (inputVal: string, option: any) => {
                const raw = option?.children ?? option?.label ?? option?.value ?? ""
                const text = Array.isArray(raw) ? raw.join(" ") : String(raw)
                return text
                  .toLowerCase()
                  .replace(/\s+/g, "")
                  .includes(String(inputVal).toLowerCase().replace(/\s+/g, ""))
              },
              onSearch: setClubSearchValue,
              optionFilterProp: "children",
            }}
          >
            {Array.isArray(clubOptions) ? (
              clubOptions.map(renderClubOption)
            ) : (
              <>
                {clubOptions.sameLeague.length > 0 ? (
                  <Select.OptGroup label={leagueGroupLabel}>
                    {clubOptions.sameLeague.map(renderClubOption)}
                  </Select.OptGroup>
                ) : null}
                {clubOptions.organizationGroups.map((organizationGroup) =>
                  organizationGroup.clubs.length > 0 ? (
                    <Select.OptGroup
                      key={`organization_group_${organizationGroup.organizationId}`}
                      label={organizationGroup.organizationName}
                    >
                      {organizationGroup.clubs.map(renderClubOption)}
                    </Select.OptGroup>
                  ) : null,
                )}
              </>
            )}
          </Select>
        </Col>
        <Col xs={24} md={11}>
          <Text type="secondary">
            This club will choose its own roster and pool after accepting the invitation.
          </Text>
        </Col>
        <Col xs={24} md={3}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Tooltip title="Remove this club from the meet">
              <Button
                danger
                type="text"
                shape="circle"
                aria-label="Remove club from meet"
                icon={<AiOutlineCloseCircle style={{ fontSize: "1.35rem" }} />}
                onClick={() => onRemoveClub(club.clubId || -1)}
              />
            </Tooltip>
          </div>
        </Col>
      </Row>
    </Card>
  )
}

function VirtualClubsSummary({
  clubSeasonsInMeet,
}: {
  clubSeasonsInMeet: {
    club: IClub
    season: { seasonId: number; seasonName: string } | null
  }[]
}) {
  const impersonateClub = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedClub,
  )

  return (
    <Space orientation="vertical" size={12} style={{ width: "100%" }}>
      <Text type="secondary">
        Add the clubs you want to invite. Each club will choose its own roster
        and pool after accepting the invitation.
      </Text>
      {clubSeasonsInMeet.length === 0 ? (
        <Empty description="No clubs added yet." />
      ) : (
        <Listy
          rowKey={(clubSeason) => clubSeason.club.clubId || clubSeason.club.name_long || clubSeason.club.nameLong || "club"}
          items={clubSeasonsInMeet}
          styles={{
            root: { border: "1px solid #f0f0f0", borderRadius: 8 },
            item: { padding: "8px 12px", borderBottom: "1px solid #f0f0f0" },
          }}
          itemRender={(clubSeason) => {
            const isPrimaryClub =
              clubSeason.club.clubId === impersonateClub?.clubId

            return (
              <Space
                orientation="vertical"
                size={2}
                style={{ width: "100%" }}
              >
                <Space wrap>
                  <Text strong>
                    {clubSeason.club.nameLong || clubSeason.club.name_long}
                  </Text>
                  {isPrimaryClub ? <Tag color="blue">Your club</Tag> : null}
                </Space>
                <Text type="secondary">
                  {isPrimaryClub
                    ? "Your club will finish setup in the next step."
                    : "This club will choose its own roster after accepting."}
                </Text>
              </Space>
            )
          }}
        />
      )}
    </Space>
  )
}

function VirtualClubsManagerModal({
  visible,
  clubSeasonsInMeet,
  availableClubs,
  leagueClubs,
  organizationGroups,
  onCancel,
  onSave,
  onAddClub,
  onSwapClub,
  onRemoveClub,
}: {
  visible: boolean
  clubSeasonsInMeet: {
    club: IClub
    season: { seasonId: number; seasonName: string } | null
  }[]
  availableClubs: IClub[]
  leagueClubs: IClub[]
  organizationGroups: IVirtualOrganizationGroup[]
  onCancel: () => void
  onSave: () => void
  onAddClub: () => void
  onSwapClub: (
    oldClubId: number,
    newClubId: number,
    seasonId: number | null,
    seasonName: string | null,
  ) => void
  onRemoveClub: (clubId: number) => void
}) {
  const impersonateClub = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedClub,
  )
  const clubsAlreadyInMeet = clubSeasonsInMeet.map((cl) => cl.club.clubId || -1)
  const remainingClubs = availableClubs.filter(
    (clubOption) =>
      !clubsAlreadyInMeet
        .filter((clubId) => typeof clubId === "number" && clubId > 0)
        .includes(clubOption.clubId || -1),
  )

  return (
    <Modal
      title="Add Clubs"
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="done" type="primary" shape="round" onClick={onSave}>
          Done
        </Button>,
      ]}
      width={860}
    >
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Text type="secondary">
          Add each participating team you want to invite. Each club will choose
          its own roster and pool after accepting.
        </Text>
        <Card
          size="small"
          style={{ borderRadius: 12, background: "#fafcff" }}
          styles={{ body: { padding: 10 } }}
        >
          <Row gutter={[12, 8]} style={{ marginBottom: 8 }}>
            <Col xs={24} md={10}>
              <Text type="secondary">Club</Text>
            </Col>
            <Col xs={24} md={11}>
              <Text type="secondary">Invitation Setup</Text>
            </Col>
            <Col xs={24} md={3} />
          </Row>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={10}>
              <Select
                disabled
                size="middle"
                style={{ width: "100%" }}
                value={impersonateClub?.clubId || undefined}
              >
                <Select.Option value={impersonateClub?.clubId || -1}>
                  {impersonateClub?.nameLong || impersonateClub?.name_long}
                </Select.Option>
              </Select>
            </Col>
            <Col xs={24} md={14}>
              <Text type="secondary">
                Your club will choose its own roster and pool in the next step.
              </Text>
            </Col>
          </Row>
        </Card>
        {clubSeasonsInMeet
          .filter(
            (cl) =>
              cl.club.clubId !== impersonateClub?.clubId,
          )
          .map((cSIM) => (
            <VirtualClubRow
              key={`club_season_item_${cSIM.club.clubId}_${cSIM.season}`}
              club={cSIM.club}
              clubSeasonsInMeet={clubSeasonsInMeet}
              availableClubs={availableClubs}
              leagueClubs={leagueClubs}
              organizationGroups={organizationGroups}
              onSwapClub={onSwapClub}
              onRemoveClub={onRemoveClub}
            />
          ))}
        {remainingClubs.length > 0 ? (
          <Button
            type="dashed"
            size="large"
            block
            icon={<PlusOutlined />}
            onClick={onAddClub}
          >
            Add Another Team
          </Button>
        ) : null}
      </Space>
    </Modal>
  )
}

function RegularClubsInMeetDisplay({
  clubSeasonsInMeet,
  availableClubs,
  seasonId,
  seasonName,
  homeClub,
  pool,
  onOpenPoolPicker,
  hidePool = false,
  divisionOptions = [],
  clubDivisionAssignments = {},
  onClubDivisionChange,
}: {
  clubSeasonsInMeet: {
    club: IClub
    season: { seasonId: number; seasonName: string } | null
  }[]
  availableClubs: IClub[]
  seasonId: number
  seasonName: string
  homeClub: number | null
  pool: IMeetPool | null
  onOpenPoolPicker: () => void
  hidePool?: boolean
  divisionOptions?: string[]
  clubDivisionAssignments?: Record<number, string | null>
  onClubDivisionChange?: (clubId: number, divisionName: string | null) => void
}) {
  const impersonateClub = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedClub,
  )
  const [clubsToSelect, setClubsToSelect] = useState<IClub[]>([])

  useEffect(() => {
    const clubsInMeet = clubSeasonsInMeet.map((cS) => cS.club.clubId || -1)
    const newClubsToSelect = availableClubs.filter(
      (aC) => !clubsInMeet.includes(aC.clubId || -1),
    )
    setClubsToSelect(newClubsToSelect)
  }, [availableClubs, clubSeasonsInMeet])

  const selectedClubIds = clubSeasonsInMeet
    .map((clubSeason) => clubSeason.club.clubId)
    .filter((clubId): clubId is number => typeof clubId === "number" && clubId > 0)

  const getSelectableClubsForRow = (currentClubId: number | undefined) =>
    availableClubs.filter((clubOption) => {
      const optionClubId = clubOption.clubId
      if (typeof optionClubId !== "number") {
        return false
      }

      return optionClubId === currentClubId || !selectedClubIds.includes(optionClubId)
    })

  const poolCourseLabel = !pool?.course
    ? "Course unknown"
    : pool.course === "SCY"
    ? `SCY (${pool.length ?? "-"}y)`
    : pool.course === "SCM" || pool.course === "LCM"
    ? `${pool.course} (${pool.length != null ? `${pool.length}m` : "-"})`
    : `${pool.course} (${
        pool.length != null
          ? pool.unitOfMeasure === 1
            ? `${pool.length}m`
            : DistanceFormatter.metersFromYards(pool.length)
          : "-"
      })`

  const hostClubSummary = clubSeasonsInMeet.find(
    (clubSeason) => clubSeason.club.clubId === homeClub,
  )

  const orderedClubSeasonsInMeet = [...clubSeasonsInMeet].sort((leftClubSeason, rightClubSeason) => {
    const leftIsPrimary = leftClubSeason.club.clubId === impersonateClub?.clubId
    const rightIsPrimary = rightClubSeason.club.clubId === impersonateClub?.clubId

    if (leftIsPrimary === rightIsPrimary) {
      return 0
    }

    return leftIsPrimary ? -1 : 1
  })

  return (
    <div className="in-person-clubs-shell">
      <div className="in-person-club-list-card">
        <div className="in-person-club-list">
          {orderedClubSeasonsInMeet.map((clubSeason) => {
            const currentClubId = clubSeason.club.clubId || undefined
            const isPrimaryClub = currentClubId === impersonateClub?.clubId
            const isHomeClub = homeClub === currentClubId
            const selectableClubs = getSelectableClubsForRow(currentClubId)

            return (
              <div
                key={`club_season_row_${clubSeason.club.clubId}_${clubSeason.season || -1}`}
                className="in-person-club-row"
              >
                <Tooltip title={isHomeClub ? "Host Club selected" : "Set as Host Club"}>
                  <Button
                    type="text"
                    shape="circle"
                    className={`in-person-club-host-button${isHomeClub ? " is-active" : ""}`}
                    aria-label={isHomeClub ? "Host Club selected" : "Set as Host Club"}
                    icon={<TiHomeOutline />}
                    onClick={() =>
                      componentUpdateListner.emit(
                        "setHomeClub",
                        clubSeason.club.clubId || null,
                      )
                    }
                  />
                </Tooltip>

                <div
                  className={`in-person-club-field${isPrimaryClub ? " has-chip" : ""}${
                    isHomeClub ? " has-host-chip" : ""
                  }`}
                >
                  {isPrimaryClub ? (
                    <>
                      {divisionOptions.length > 0 && currentClubId != null ? (
                        <div style={{ display: "flex", gap: 6, alignItems: "center", width: "100%" }}>
                          <div style={{ flex: "2 1 0", minWidth: 0 }}>
                            <div className="edit-readonly-box">{getClubDisplayName(clubSeason.club)}</div>
                          </div>
                          <Select
                            size="large"
                            style={{ flex: "1 1 0", minWidth: 0 }}
                            placeholder="Division…"
                            allowClear
                            value={clubDivisionAssignments[currentClubId] ?? undefined}
                            onChange={(val: string | undefined) =>
                              onClubDivisionChange?.(currentClubId, val ?? null)
                            }
                            options={divisionOptions.map((d) => ({ value: d, label: d }))}
                          />
                        </div>
                      ) : (
                        <div className="edit-readonly-box">{getClubDisplayName(clubSeason.club)}</div>
                      )}
                      <Tag
                        color="blue"
                        className={`in-person-club-field-chip${isHomeClub ? " in-person-club-field-chip-secondary" : ""}`}
                      >
                        Your Club
                      </Tag>
                      {isHomeClub ? (
                        <Tag color="gold" className="in-person-club-field-chip">Host Club</Tag>
                      ) : null}
                      <div className="in-person-club-mobile-chip-row">
                        <Tag color="blue">Your Club</Tag>
                        {isHomeClub ? <Tag color="gold">Host Club</Tag> : null}
                      </div>
                    </>
                  ) : (
                    <>
                      {divisionOptions.length > 0 && currentClubId != null ? (
                        <div style={{ display: "flex", gap: 6, alignItems: "center", width: "100%" }}>
                          <Select
                            size="large"
                            style={{ flex: "2 1 0", minWidth: 0 }}
                            value={currentClubId}
                            placeholder="Choose Club"
                            onChange={(newClubId) => {
                              componentUpdateListner.emit(
                                "swapClubsInMeet",
                                clubSeason.club.clubId as number,
                                newClubId,
                                seasonId,
                                seasonName,
                              )
                            }}
                            options={selectableClubs.map((clubOption) => ({
                              value: clubOption.clubId || -1,
                              label: getClubDisplayName(clubOption),
                            }))}
                          />
                          <Select
                            size="large"
                            style={{ flex: "1 1 0", minWidth: 0 }}
                            placeholder="Division…"
                            allowClear
                            value={clubDivisionAssignments[currentClubId] ?? undefined}
                            onChange={(val: string | undefined) =>
                              onClubDivisionChange?.(currentClubId, val ?? null)
                            }
                            options={divisionOptions.map((d) => ({ value: d, label: d }))}
                          />
                        </div>
                      ) : (
                        <Select
                          size="large"
                          style={{ width: "100%" }}
                          value={currentClubId}
                          placeholder="Choose Club"
                          onChange={(newClubId) => {
                            componentUpdateListner.emit(
                              "swapClubsInMeet",
                              clubSeason.club.clubId as number,
                              newClubId,
                              seasonId,
                              seasonName,
                            )
                          }}
                          options={selectableClubs.map((clubOption) => ({
                            value: clubOption.clubId || -1,
                            label: getClubDisplayName(clubOption),
                          }))}
                        />
                      )}
                      {isHomeClub ? (
                        <Tag color="gold" className="in-person-club-field-chip">Host Club</Tag>
                      ) : null}
                      {isHomeClub ? (
                        <div className="in-person-club-mobile-chip-row">
                          <Tag color="gold">Host Club</Tag>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>

                {isPrimaryClub ? (
                  <div />
                ) : (
                  <Tooltip title="Remove this club from the meet">
                    <Button
                      danger
                      type="text"
                      shape="circle"
                      aria-label="Remove club from meet"
                      icon={<AiOutlineCloseCircle style={{ fontSize: "1.2rem" }} />}
                      onClick={() =>
                        componentUpdateListner.emit(
                          "removeClub",
                          clubSeason.club.clubId || -1,
                        )
                      }
                    />
                  </Tooltip>
                )}


              </div>
            )
          })}
        </div>

        {clubsToSelect.length > 0 ? (
          <Button
            block
            type="dashed"
            size="large"
            icon={<PlusOutlined />}
            className="in-person-add-club"
            onClick={() => componentUpdateListner.emit("addNextClub")}
          >
            Add Club Participating in Meet
          </Button>
        ) : null}
      </div>

      {!hidePool && hostClubSummary ? (
        <div className="in-person-pool-panel">
          <div className="in-person-pool-panel-header">
            <div className="in-person-pool-panel-copy">
              <span className="edit-field-label">Pool & Venue</span>
              <Typography.Title level={5} style={{ margin: "4px 0 0" }}>
                {pool?.poolName || "No host pool selected"}
              </Typography.Title>
              <Typography.Paragraph type="secondary" style={{ margin: "6px 0 0" }}>
                Host Club: {getClubDisplayName(hostClubSummary.club)}
              </Typography.Paragraph>
            </div>
            <Button
              type={pool ? "default" : "primary"}
              shape="round"
              onClick={onOpenPoolPicker}
            >
              {pool ? "Change Pool" : "Select Pool"}
            </Button>
          </div>

          {pool ? (
            <div className="in-person-pool-details">
              <div className="edit-field-stack">
                <span className="edit-field-label">Pool Club</span>
                <div className="edit-readonly-box">{pool.clubName || "Club pool"}</div>
              </div>
              <div className="edit-field-stack">
                <span className="edit-field-label">Course</span>
                <div className="edit-readonly-box">{poolCourseLabel}</div>
              </div>
              <div className="edit-field-stack in-person-pool-detail--full">
                <span className="edit-field-label">Pool Address</span>
                <div className="edit-readonly-box">{pool.address || "No address stored"}</div>
              </div>
            </div>
          ) : (
            <Alert
              type="warning"
              showIcon
              title="Pool required"
              description="Select the pool for the Host Club before creating the meet."
            />
          )}
        </div>
      ) : null}
    </div>
  )
}

interface AssignEventsToSessionsModalProps {
  open: boolean
  sessions: ChampionshipSession[]
  events: TMeetEvent[]
  sessionEventAssignments: Record<string, number[]>
  onClose: () => void
  onConfirm: (newAssignments: Record<string, number[]>) => void
}

function AssignEventsToSessionsModal({
  open,
  sessions,
  events,
  sessionEventAssignments,
  onClose,
  onConfirm,
}: AssignEventsToSessionsModalProps) {
  const [assignmentMap, setAssignmentMap] = useState<Record<number, string | null>>({})
  const [lockedMap, setLockedMap] = useState<Record<number, string | null>>({})
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set())

  // Initialize when modal opens
  useEffect(() => {
    if (!open) return
    const map: Record<number, string | null> = {}
    events.forEach((e) => { map[e.swimEventId] = null })
    sessions.forEach((s) => {
      ;(sessionEventAssignments[s.id] ?? []).forEach((id) => { map[id] = s.id })
    })
    setLockedMap(map)
    setAssignmentMap(map)
    setCheckedIds(new Set())
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Compute range as [minIdx, maxIdx] between the first and last explicitly clicked row.
  // Avoids iterating Set<number> (not allowed at es5 target without downlevelIteration).
  const selectedRange = useMemo<[number, number] | null>(() => {
    const indices: number[] = []
    events.forEach((e, i) => {
      if (checkedIds.has(e.swimEventId)) indices.push(i)
    })
    if (indices.length === 0) return null
    return [Math.min(...indices), Math.max(...indices)]
  }, [checkedIds, events])

  const selectionSize = selectedRange !== null ? selectedRange[1] - selectedRange[0] + 1 : 0

  const isInFullSelection = (index: number) =>
    selectedRange !== null && index >= selectedRange[0] && index <= selectedRange[1]

  // Count rows that are in the range but not explicitly clicked (auto-filled)
  const autoFilledCount = useMemo(() => {
    if (!selectedRange) return 0
    const [minIdx, maxIdx] = selectedRange
    let count = 0
    for (let i = minIdx; i <= maxIdx; i++) {
      if (!checkedIds.has(events[i].swimEventId)) count++
    }
    return count
  }, [selectedRange, checkedIds, events])

  const sessionLookup = useMemo(() => {
    const map: Record<string, { idx: number; label: string }> = {}
    sessions.forEach((s, i) => {
      const tl = s.type === "timedFinals" ? "TF" : s.type === "prelims" ? "P" : "F"
      map[s.id] = { idx: i, label: `Session ${i + 1} (${tl})` }
    })
    return map
  }, [sessions])

  // Pair prelims↔finals by position order (1st prelims ↔ 1st finals, etc.)
  const { prelimsToFinalsMap, finalsSessionIds } = useMemo(() => {
    const prelimsSessions = sessions.filter((s) => s.type === "prelims")
    const finalsSessions = sessions.filter((s) => s.type === "finals")
    const p2f: Record<string, string> = {}
    prelimsSessions.forEach((p, i) => {
      if (finalsSessions[i]) p2f[p.id] = finalsSessions[i].id
    })
    return { prelimsToFinalsMap: p2f, finalsSessionIds: new Set(finalsSessions.map((s) => s.id)) }
  }, [sessions])

  const assignToSession = (sessionId: string) => {
    if (!selectedRange) return
    const [minIdx, maxIdx] = selectedRange
    const idsToAssign = events.slice(minIdx, maxIdx + 1).map((e) => e.swimEventId)
    setAssignmentMap((prev) => {
      const next = { ...prev }
      idsToAssign.forEach((id) => { next[id] = sessionId })
      return next
    })
    setCheckedIds(new Set())
  }

  const clearAll = () => {
    const next: Record<number, string | null> = {}
    events.forEach((e) => { next[e.swimEventId] = null })
    setAssignmentMap(next)
    setCheckedIds(new Set())
  }

  const handleConfirm = () => {
    const newAssignments: Record<string, number[]> = {}
    sessions.forEach((s) => { newAssignments[s.id] = [] })
    // Preserve event ordering from the original events array
    events.forEach((e) => {
      const sid = assignmentMap[e.swimEventId]
      if (sid && newAssignments[sid]) {
        newAssignments[sid].push(e.swimEventId)
      }
    })
    // Mirror each prelims session's events to its paired finals session
    Object.entries(prelimsToFinalsMap).forEach(([prelimsId, finalsId]) => {
      newAssignments[finalsId] = [...newAssignments[prelimsId]]
    })
    onConfirm(newAssignments)
  }

  const unassignedCount = events.filter((e) => !assignmentMap[e.swimEventId]).length

  const columns = [
    {
      key: "check",
      width: 36,
      render: (_: unknown, record: TMeetEvent, index: number) => {
        const inSel = isInFullSelection(index)
        const isAutoFilled = inSel && !checkedIds.has(record.swimEventId)
        return (
          <Checkbox
            checked={inSel}
            indeterminate={isAutoFilled}
            onChange={(e) => {
              setCheckedIds((prev) => {
                const next = new Set(prev)
                if (e.target.checked) next.add(record.swimEventId)
                else next.delete(record.swimEventId)
                return next
              })
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )
      },
    },
    {
      title: "#",
      key: "num",
      width: 55,
      render: (_: unknown, record: TMeetEvent) =>
        resolveEventNumber(record.displayNumber, record.eventNumber),
    },
    { title: "Age Group", dataIndex: "ageGroup" as const, key: "ag", width: 90 },
    { title: "Gender", dataIndex: "gender" as const, key: "ge", width: 70 },
    { title: "Distance", dataIndex: "distance" as const, key: "di", width: 70 },
    { title: "Stroke", dataIndex: "stroke" as const, key: "stroke" },
    {
      title: "Session",
      key: "session",
      width: 110,
      render: (_: unknown, record: TMeetEvent) => {
        const sid = assignmentMap[record.swimEventId]
        if (!sid) return <span style={{ color: "#bbb" }}>—</span>
        const info = sessionLookup[sid]
        if (!info) return null
        const color = SESSION_COLORS_LIST[info.idx % SESSION_COLORS_LIST.length]
        const isPending = sid !== lockedMap[record.swimEventId]
        const pairedFinalsId = prelimsToFinalsMap[sid]
        const pairedFinalsInfo = pairedFinalsId ? sessionLookup[pairedFinalsId] : null
        return (
          <Space size={2} orientation="vertical">
            {isPending ? (
              <Tag
                style={{
                  fontSize: 11,
                  borderColor: color,
                  color: color,
                  background: `${color}18`,
                  borderStyle: "dashed",
                }}
              >
                {info.label}
              </Tag>
            ) : (
              <Tag color={color} style={{ fontSize: 11 }}>
                {info.label}
              </Tag>
            )}
            {pairedFinalsInfo && (
              <Tag
                icon={<LockOutlined />}
                style={{ fontSize: 11, color: "#888", borderColor: "#d9d9d9", background: "#fafafa" }}
              >
                {pairedFinalsInfo.label}
              </Tag>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <Modal
      title="Assign Events to Sessions"
      open={open}
      onCancel={onClose}
      width={740}
      destroyOnHidden
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button size="small" danger onClick={clearAll}>
            Clear all
          </Button>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" disabled={unassignedCount > 0} onClick={handleConfirm}>
              Confirm
            </Button>
          </Space>
        </div>
      }
    >
      {/* Toolbar: selection count + per-session assign buttons */}
      <div style={{ minHeight: 36, marginBottom: 10 }}>
        {selectionSize > 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Typography.Text style={{ fontSize: 13, color: "#555", flexShrink: 0 }}>
              {selectionSize} event{selectionSize !== 1 ? "s" : ""} selected
              {autoFilledCount > 0 && (
                <span style={{ color: "#faad14", marginLeft: 4 }}>
                  ({autoFilledCount} auto-filled)
                </span>
              )}
              {" — assign to:"}
            </Typography.Text>
            {sessions.filter((s) => !finalsSessionIds.has(s.id)).map((s) => {
              const info = sessionLookup[s.id]
              const color = SESSION_COLORS_LIST[info.idx % SESSION_COLORS_LIST.length]
              return (
                <Button
                  key={s.id}
                  size="small"
                  style={{ borderColor: color, color: color }}
                  onClick={() => assignToSession(s.id)}
                >
                  {info?.label ?? s.id}
                </Button>
              )
            })}
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Click checkboxes (or rows) to select a range, then click a session button to assign.
            </Typography.Text>
            {unassignedCount > 0 && (
              <Tag color="warning" style={{ marginLeft: 8 }}>
                {unassignedCount} unassigned
              </Tag>
            )}
          </div>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={events}
        rowKey="swimEventId"
        pagination={false}
        size="small"
        scroll={{ y: 420 }}
        onRow={(record, index) => ({
          style: {
            background: (() => {
              if (index !== undefined && isInFullSelection(index)) return "#e6f4ff"
              const sid = assignmentMap[record.swimEventId]
              if (!sid) return undefined
              const info = sessionLookup[sid]
              if (!info) return undefined
              return `${SESSION_COLORS_LIST[info.idx % SESSION_COLORS_LIST.length]}15`
            })(),
            cursor: "pointer",
          },
          onClick: () => {
            setCheckedIds((prev) => {
              const next = new Set(prev)
              if (next.has(record.swimEventId)) next.delete(record.swimEventId)
              else next.add(record.swimEventId)
              return next
            })
          },
        })}
      />
    </Modal>
  )
}

export default function CreateMeetModal({
  visible,
  setVisible,
  refreshSchedule,
  season,
}: ICreateMeetModalProps) {
  const aliasedUser = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedUser,
  )
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const impersonateClub = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedClub,
  )
  const actingUser = aliasedUser || user
  const actingClubIdForPoolCreate = impersonateClub?.clubId ?? user?.clubId ?? null
  const { isFeatureLocked, openLockedModal } = useFeatureAccess()
  // Flip to false once Multi-Session Meets are ready to ship
  const MULTI_SESSION_COMING_SOON = true
  const poolCreateBlockedReason =
    user?.role === 2 && (!aliasedUser || aliasedUser.clubId !== actingClubIdForPoolCreate)
      ? "Swimmingly admins can add pools from meet setup only when aliased into an eligible club user for this club. Switch into that club user first, then try again."
      : null

  const [isVirtual, setIsVirtual] = useState(false)
  const [hoveredMeetKind, setHoveredMeetKind] = useState<"inperson" | "virtual" | null>(null)
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const [loading, setLoading] = useState(false)
  // start empty so user must explicitly pick dates each time modal opens
  const [meetEndDate, setMeetEndDate] = useState<Dayjs | null>(null)
  const [meetStartDate, setMeetStartDate] = useState<Dayjs | null>(null)
  const [meetEntriesDeadlineEnabled, setMeetEntriesDeadlineEnabled] =
    useState(false)
  const [meetEntriesDeadline, setMeetEntriesDeadline] = useState<Dayjs | null>(null)
  const [autoMarkHeatSheetReadyAfterDeadline, setAutoMarkHeatSheetReadyAfterDeadline] =
    useState(false)
  const [meetEntriesDeadlineInfoVisible, setMeetEntriesDeadlineInfoVisible] =
    useState(false)
  const [meetName, setMeetName] = useState("")
  const [availableLeagueEventOrders, setAvailableLeagueEventOrders] = useState<
    { eventOrderId: number; name: string }[]
  >([])
  const [availableClubEventOrders, setAvailableClubEventOrders] = useState<
    { eventOrderId: number; name: string }[]
  >([])
  const [availableEventOrders, setAvailableEventOrders] = useState<
    { eventOrderId: number; name: string }[]
  >([])
  const [eventOrder, setEventOrder] =
    useState<{
      eventOrderId: number
      name: string
    } | null>(null)
  const [createMeetStep, setCreateMeetStep] = useState<"inperson-virtual" | "type-select" | "edit" | "review">("inperson-virtual")
  const [meetTypeChoice, setMeetTypeChoice] = useState<"singleSession" | "multiSession" | null>(null)
  const [championshipName, setChampionshipName] = useState("")
  const [sessions, setSessions] = useState<ChampionshipSession[]>([])
  const [enableDivisions, setEnableDivisions] = useState(false)
  const [finalsConfig, setFinalsConfig] = useState<{
    bFinalEnabled: boolean
    cFinalEnabled: boolean
    dFinalEnabled: boolean
    direction: "slowestFirst" | "fastestFirst"
  }>({ bFinalEnabled: true, cFinalEnabled: false, dFinalEnabled: false, direction: "slowestFirst" })
  const [divisions, setDivisions] = useState<string[]>([])
  const [divisionInput, setDivisionInput] = useState("")
  const [clubDivisionAssignments, setClubDivisionAssignments] = useState<Record<number, string | null>>({})
  const [eventOrderInfoVisible, setEventOrderInfoVisible] = useState(false)
  const [meetTypeInfoVisible, setMeetTypeInfoVisible] = useState(false)
  const [teamDivisionsInfoVisible, setTeamDivisionsInfoVisible] = useState(false)
  const [assignEventsModalOpen, setAssignEventsModalOpen] = useState(false)
  const [importEventOrderModalVisible, setImportEventOrderModalVisible] = useState(false)
  const [reviewSectionsConfirmed, setReviewSectionsConfirmed] = useState<IReviewSectionsState>(
    DEFAULT_REVIEW_SECTIONS_STATE,
  )
  const [eventOrderPreviewOpened, setEventOrderPreviewOpened] = useState(false)
  const [eventOrderPreviewVisible, setEventOrderPreviewVisible] = useState(false)
  const [eventOrderPreviewLoading, setEventOrderPreviewLoading] = useState(false)
  const [eventOrderPreviewName, setEventOrderPreviewName] = useState<string | null>(null)
  const [eventOrderPreviewEvents, setEventOrderPreviewEvents] = useState<TMeetEvent[]>([])
  const [eventOrderSessionNumbers, setEventOrderSessionNumbers] = useState<number[]>([])
  const [sessionEventAssignments, setSessionEventAssignments] = useState<Record<string, number[]>>({})
  const [availableClubsToAddToMeet, setAvailableClubsToAddToMeet] = useState<
    IClub[]
  >([])
  const [clubSeasonsInMeet, setClubSeasonsInMeet] = useState<
    { club: IClub; season: { seasonId: number; seasonName: string } | null }[]
  >(
    impersonateClub
      ? [
          {
            club: impersonateClub,
            season: season
              ? { seasonId: season.seasonId, seasonName: season.seasonName }
              : null,
          },
        ]
      : [],
  )
  const [homeClub, setHomeClub] = useState<number | null>(
    impersonateClub?.clubId || null,
  )
  const [hasIndividualEntryLimit, setHasIndividualEntryLimit] = useState(true)
  const [individualEntryLimit, setIndividualEntryLimit] = useState<number>(3)
  const [hasIndividualScoringLimit, setHasIndividualScoringLimit] =
    useState(true)
  const [individualScoringLimit, setIndividualScoringLimit] = useState(5)
  const [hasRelayEntryLimit, setHasRelayEntryLimit] = useState(true)
  const [relayEntryLimit, setRelayEntryLimit] = useState<number>(2)
  const [hasTotalEntryLimit, setHasTotalEntryLimit] = useState(false)
  const [totalEntryLimit, setTotalEntryLimit] = useState<number | null>(null)
  const [hasRelayScoringLimit, setHasRelayScoringLimit] = useState(true)
  const [relayScoringLimit, setRelayScoringLimit] = useState(5)
  const [poolOptions, setPoolOptions] = useState<IMeetPool[]>([])
  const [genericPoolOptions, setGenericPoolOptions] = useState<IMeetPool[]>([])
  const [pool, setPool] = useState<IMeetPool | null>(null)
  const [poolPickerVisible, setPoolPickerVisible] = useState(false)
  const [poolPickerClubId, setPoolPickerClubId] = useState<number | null>(null)
  const [createPoolModalVisible, setCreatePoolModalVisible] = useState(false)
  // Per-session pool support (championship type)
  const [samePoolAllSessions, setSamePoolAllSessions] = useState(true)
  const [sessionPools, setSessionPools] = useState<Record<string, IMeetPool | null>>({})
  const [poolPickerTargetSessionId, setPoolPickerTargetSessionId] = useState<string | null>(null)
  const [virtualCourse, setVirtualCourse] =
    useState<"SCY" | "SCM" | "LCM">("SCY")
  const [individualScoringSettings, setIndividualScoringSettings] = useState([
    { place: 1, points: 5 },
    { place: 2, points: 3 },
    { place: 3, points: 1 },
  ])
  const [relayScoringSettings, setRelayScoringSettings] = useState([
    { place: 1, points: 5 },
  ])
  const [virtualClubsModalVisible, setVirtualClubsModalVisible] = useState(false)
  const [virtualDraftClubSeasonsInMeet, setVirtualDraftClubSeasonsInMeet] = useState<
    {
      club: IClub
      season: { seasonId: number; seasonName: string } | null
    }[]
  >([])
  const [virtualLeagueClubs, setVirtualLeagueClubs] = useState<IClub[]>([])
  const [virtualOrganizationGroups, setVirtualOrganizationGroups] = useState<
    IVirtualOrganizationGroup[]
  >([])
  const [organizerInvitationIdToRespond, setOrganizerInvitationIdToRespond] =
    useState<number | null>(null)
  const [organizerVirtualMeetIdToRespond, setOrganizerVirtualMeetIdToRespond] =
    useState<number | null>(null)
  const [organizerInvitationSeasonId, setOrganizerInvitationSeasonId] =
    useState<number | null>(null)
  const [organizerInvitationModalVisible, setOrganizerInvitationModalVisible] =
    useState(false)
  const nextVirtualTempClubIdRef = useRef(-1)

  // clear dates every time the modal opens so user must select them
  useEffect(() => {
    if (visible) {
      setMeetStartDate(null)
      setMeetEndDate(null)
    }
  }, [visible])

  useEffect(() => {
    if (isVirtual || meetStartDate) {
      return
    }

    setMeetEntriesDeadlineEnabled(false)
    setMeetEntriesDeadline(null)
    setAutoMarkHeatSheetReadyAfterDeadline(false)
  }, [isVirtual, meetStartDate])

  const openVirtualClubsManager = useCallback(() => {
    setVirtualDraftClubSeasonsInMeet(cloneDeep(clubSeasonsInMeet))
    setVirtualClubsModalVisible(true)
  }, [clubSeasonsInMeet])

  const closeVirtualClubsManager = useCallback(() => {
    setVirtualClubsModalVisible(false)
    setVirtualDraftClubSeasonsInMeet([])
  }, [])

  const saveVirtualClubsManager = useCallback(() => {
    const hasUnselectedClubRow = virtualDraftClubSeasonsInMeet.some(
      (clubSeason) =>
        !clubSeason.club.clubId || clubSeason.club.clubId < 1,
    )

    if (hasUnselectedClubRow) {
      message.error("Choose a club for every added row before saving.")
      return
    }

    setClubSeasonsInMeet(cloneDeep(virtualDraftClubSeasonsInMeet))
    setVirtualClubsModalVisible(false)
    setVirtualDraftClubSeasonsInMeet([])
  }, [virtualDraftClubSeasonsInMeet])

  const openPoolPicker = useCallback(() => {
    setPoolPickerClubId(homeClub)
    setPoolPickerTargetSessionId(null)
    setPoolPickerVisible(true)
  }, [homeClub])

  const openSessionPoolPicker = useCallback((sessionId: string) => {
    setPoolPickerClubId(homeClub)
    setPoolPickerTargetSessionId(sessionId)
    setPoolPickerVisible(true)
  }, [homeClub])

  const closePoolPicker = useCallback(() => {
    setPoolPickerVisible(false)
  }, [])

  const loadSeasonPools = useCallback(async (): Promise<IMeetPool[]> => {
    if (!season?.seasonId) {
      setPoolOptions([])
      setGenericPoolOptions([])
      return []
    }

    const data = await swimminglyApi.get(gen(`/api/getSeasonHomePools/${season.seasonId}`))
    if (data.status !== "success") {
      return []
    }

    const poolOptionData = data.pools.map((pool: any) => ({
      poolName: pool.pool_name,
      poolId: pool.poolId,
      address: pool.address,
      length: pool.pool_length,
      course: pool.course_label,
      clubId: pool.clubId ?? null,
      clubName: pool.clubName ?? null,
      description: pool.description ?? null,
      lat: pool.lat ?? null,
      lng: pool.lng ?? null,
      unitOfMeasure: pool.unit_of_measure ?? null,
      numLanes: pool.numLanes ?? null,
      primaryPool: !!pool.primaryPool,
    }))

    const genericPoolOptionData = (data.genericPools || []).map((pool: any) => ({
      poolName: pool.pool_name,
      poolId: pool.poolId,
      address: pool.address,
      length: pool.pool_length,
      course: pool.course_label,
      clubId: null,
      clubName: null,
      description: pool.description ?? null,
      lat: pool.lat ?? null,
      lng: pool.lng ?? null,
      unitOfMeasure: pool.unit_of_measure ?? null,
      numLanes: pool.numLanes ?? null,
      primaryPool: false,
    }))

    setPoolOptions(poolOptionData)
    setGenericPoolOptions(genericPoolOptionData)
    return poolOptionData
  }, [season?.seasonId])

  const addVirtualDraftClub = useCallback(() => {
    setVirtualDraftClubSeasonsInMeet((currentClubSeasonsInMeet) => {
      const clubIdsInMeet = currentClubSeasonsInMeet.map((clubSeason) => clubSeason.club.clubId)
      const clubsToSelect = availableClubsToAddToMeet.filter(
        (availableClub) => !clubIdsInMeet.includes(availableClub.clubId),
      )

      if (clubsToSelect.length === 0) {
        return currentClubSeasonsInMeet
      }

      return [
        ...currentClubSeasonsInMeet,
        {
          club: {
            clubId: nextVirtualTempClubIdRef.current,
            nameLong: "",
            name: "",
          },
          season: null,
        },
      ]
    })
    nextVirtualTempClubIdRef.current -= 1
  }, [availableClubsToAddToMeet])

  const removeVirtualDraftClub = useCallback((clubId: number) => {
    setVirtualDraftClubSeasonsInMeet((currentClubSeasonsInMeet) =>
      currentClubSeasonsInMeet.filter(
        (clubSeason) => clubSeason.club.clubId !== clubId,
      ),
    )
  }, [])

  const swapVirtualDraftClub = useCallback(
    (
      oldClubId: number,
      newClubId: number,
      seasonId: number | null,
      seasonName: string | null,
    ) => {
      setVirtualDraftClubSeasonsInMeet((currentClubSeasonsInMeet) => {
        const clubIds = currentClubSeasonsInMeet.map(
          (clubSeasonInMeet) => clubSeasonInMeet.club.clubId,
        )

        if (clubIds.includes(newClubId)) {
          return currentClubSeasonsInMeet
        }

        const newClub = availableClubsToAddToMeet.find(
          (searchClub) => searchClub.clubId === newClubId,
        )
        if (!newClub) {
          return currentClubSeasonsInMeet
        }

        return currentClubSeasonsInMeet.map((clubSeasonInMeet) => {
          if (clubSeasonInMeet.club.clubId !== oldClubId) {
            return clubSeasonInMeet
          }

          return {
            club: newClub,
            season:
              seasonId === null || seasonName === null
                ? null
                : { seasonId, seasonName },
          }
        })
      })
    },
    [availableClubsToAddToMeet],
  )

  // set up removeClub capability for all components
  useEffect(() => {
    const removeClub = (clubId: number) => {
      const newClubsInMeet = [...clubSeasonsInMeet]
      const clubIds = newClubsInMeet.map((club) => club.club.clubId)
      const idx = clubIds.indexOf(clubId)
      if (idx !== -1) {
        if (newClubsInMeet[idx].club.clubId === homeClub)
          setHomeClub(impersonateClub?.clubId || null)
        newClubsInMeet.splice(idx, 1)
      }
      setClubSeasonsInMeet(newClubsInMeet)
    }

    componentUpdateListner.on("removeClub", removeClub)
    return () => componentUpdateListner.removeAllListeners("removeClub")
  }, [
    clubSeasonsInMeet,
    setClubSeasonsInMeet,
    homeClub,
    setHomeClub,
    impersonateClub,
  ])

  // set up ability to update scoring configuration for individual events
  useEffect(() => {
    componentUpdateListner.on(
      "updateIndividualScoringConfig",
      setIndividualScoringSettings,
    )
    return () =>
      componentUpdateListner.removeAllListeners("updateIndividualScoringConfig")
  }, [setIndividualScoringSettings])

  // set up ability to update scoring configuration for relay events
  useEffect(() => {
    componentUpdateListner.on(
      "updateRelayScoringConfig",
      setRelayScoringSettings,
    )
    return () =>
      componentUpdateListner.removeAllListeners("updateRelayScoringConfig")
  }, [setRelayScoringSettings])

  // set up ability to update a club's season - to use for virtual meets
  useEffect(() => {
    const updateSeasonForClubInMeet = (
      clubId: number,
      newSeasonId: number,
      newSeasonName: string,
    ) => {
      const newClubSeasonsInMeet = cloneDeep(clubSeasonsInMeet)
      const thisClub = newClubSeasonsInMeet.find(
        (cl) => cl.club.clubId === clubId,
      )
      if (thisClub) {
        thisClub.season = { seasonId: newSeasonId, seasonName: newSeasonName }
      }
      setClubSeasonsInMeet(newClubSeasonsInMeet)
    }
    componentUpdateListner.on(
      "updateSeasonForClubInMeet",
      updateSeasonForClubInMeet,
    )
    return () =>
      componentUpdateListner.removeAllListeners("updateSeasonForClubInMeet")
  }, [clubSeasonsInMeet, setClubSeasonsInMeet])

  // set up addNextClub capability for all components
  useEffect(() => {
    const addNextClub = () => {
      const clubIdsInMeet = clubSeasonsInMeet.map((c) => c.club.clubId)
      const clubsToSelect = availableClubsToAddToMeet.filter(
        (c) => !clubIdsInMeet.includes(c.clubId),
      )
      if (clubsToSelect.length > 0) {
        const newClubsInMeet = [...clubSeasonsInMeet]
        newClubsInMeet.push({
          club: isVirtual
            ? {
                clubId: nextVirtualTempClubIdRef.current,
                nameLong: "",
                name: "",
              }
            : clubsToSelect[0],
          season:
            season && !isVirtual
              ? { seasonId: season.seasonId, seasonName: season.seasonName }
              : null,
        })
        if (isVirtual) {
          nextVirtualTempClubIdRef.current -= 1
        }
        setClubSeasonsInMeet(newClubsInMeet)
      }
    }
    componentUpdateListner.on("addNextClub", addNextClub)
    return () => componentUpdateListner.removeAllListeners("addNextClub")
  }, [
    availableClubsToAddToMeet,
    clubSeasonsInMeet,
    setClubSeasonsInMeet,
    season,
    isVirtual,
  ])

  // set up ability to swap a club that is signed up for the meet with one that is not
  useEffect(() => {
    const swapClubsInMeet = (
      oldClubId: number,
      newClubId: number,
      seasonId: number | null,
      seasonName: string | null,
    ) => {
      const clubIds = clubSeasonsInMeet.map(
        (clubSeasonInMeet) => clubSeasonInMeet.club.clubId,
      )

      if (clubIds.includes(newClubId)) return

      const newClubSeasonsInMeet = cloneDeep(clubSeasonsInMeet)
      const newClub = availableClubsToAddToMeet.find(
        (searchClub) => searchClub.clubId === newClubId,
      )
      const idx = newClubSeasonsInMeet
        .map((nCSIM) => nCSIM.club.clubId)
        .indexOf(oldClubId)
      if (newClub) {
        if (seasonId === null || seasonName === null) {
          newClubSeasonsInMeet.splice(idx, 1, {
            club: newClub,
            season: null,
          })
        } else {
          newClubSeasonsInMeet.splice(idx, 1, {
            club: newClub,
            season: { seasonId, seasonName },
          })
        }
      }
      if (homeClub === oldClubId) {
        setHomeClub(impersonateClub?.clubId || -1)
      }
      setClubSeasonsInMeet(newClubSeasonsInMeet)
    }

    componentUpdateListner.on("swapClubsInMeet", swapClubsInMeet)
    return () => componentUpdateListner.removeAllListeners("swapClubsInMeet")
  }, [availableClubsToAddToMeet, clubSeasonsInMeet, homeClub, impersonateClub])

  // update the list of available pools for traditional meets based on the season
  useEffect(() => {
    if (season?.seasonId) {
      loadSeasonPools()
    }
  }, [loadSeasonPools, season?.seasonId])

  useEffect(() => {
    const clubIdsInMeet = new Set(
      clubSeasonsInMeet
        .map((clubSeason) => clubSeason.club.clubId)
        .filter((clubId): clubId is number => typeof clubId === "number"),
    )

    if (!isVirtual && pool?.clubId && !clubIdsInMeet.has(pool.clubId)) {
      setPool(null)
    }
  }, [clubSeasonsInMeet, isVirtual, pool])

  // set up ability to set the meet's pool from other components without prop drilling
  useEffect(() => {
    componentUpdateListner.on("setPool", setPool)
    return () => componentUpdateListner.removeAllListeners("setPool")
  }, [setPool])

  // set up ability to set the meet's home club from other components without prop drilling
  useEffect(() => {
    componentUpdateListner.on("setHomeClub", setHomeClub)
    return () => componentUpdateListner.removeAllListeners("setHomeClub")
  }, [setHomeClub])

  // set the meet's home club to the user's aliased club by default (can be overridden later by user)
  useEffect(() => {
    setHomeClub(impersonateClub?.clubId || null)
  }, [impersonateClub])

  const selectablePoolClubs = useMemo(
    () =>
      clubSeasonsInMeet
        .map((clubSeason) => ({
          clubId: clubSeason.club.clubId || -1,
          clubName: getClubDisplayName(clubSeason.club),
        }))
        .filter((club) => club.clubId > 0),
    [clubSeasonsInMeet],
  )

  const createMeetSelectablePools = useMemo(
    () =>
      poolOptions.filter((poolOption) =>
        selectablePoolClubs.some((club) => club.clubId === poolOption.clubId),
      ),
    [poolOptions, selectablePoolClubs],
  )

  // get the list of clubs that we can add to the meet based on whether or not the meet is virtual & the season
  useEffect(() => {
    if (!season?.seasonId) return
    if (!isVirtual) {
      swimminglyApi
        .get(`/api/getClubsInSeason/${season.seasonId}`)
        .then((data) => {
          if (data.status === "success") {
            setAvailableClubsToAddToMeet(data.clubs)
          }
        })
    } else {
      swimminglyApi.get(`/api/getAllClubsRawFormat`).then((data) => {
        if (data.status === "success") {
          setAvailableClubsToAddToMeet(data.clubs)
        }
      })

      if (impersonateClub?.clubId) {
        swimminglyApi
          .get(`/api/getLeagueClubsForClub/${impersonateClub.clubId}`)
          .then((data) => {
            if (data.status === "success") {
              setVirtualLeagueClubs(data.clubs)
            }
          })

        swimminglyApi
          .get(`/api/getOrganizationClubGroupsForClub/${impersonateClub.clubId}`)
          .then((data) => {
            if (data.status === "success") {
              setVirtualOrganizationGroups(data.organizations)
            }
          })
      } else {
        setVirtualLeagueClubs([])
        setVirtualOrganizationGroups([])
      }
    }
  }, [season?.seasonId, isVirtual, impersonateClub?.clubId])

  useEffect(() => {
    if (!isVirtual) {
      setVirtualLeagueClubs([])
      setVirtualOrganizationGroups([])
    }
  }, [isVirtual])

  // whenever the user's aliased club or the selected season changes, or whenever the modal is opened or
  // closed, by default choose the user's aliased club to be the only club included in the meet
  useEffect(() => {
    setClubSeasonsInMeet(
      impersonateClub
        ? [
            {
              club: impersonateClub,
              season: season
                ? { seasonId: season.seasonId, seasonName: season.seasonName }
                : null,
            },
          ]
        : [],
    )
  }, [impersonateClub, season, visible])

  // if a season is chosen or if the meet is changed from a virtual meet to a traditional meet, update
  // the season for each club to be the selected season
  useEffect(() => {
    if (season?.seasonId && !isVirtual) {
      const numBadSeasons = clubSeasonsInMeet.filter(
        (cl) => cl.season?.seasonId !== season.seasonId,
      ).length
      if (numBadSeasons > 0) {
        setClubSeasonsInMeet(
          clubSeasonsInMeet.map((cl) => ({
            ...cl,
            season: {
              seasonId: season.seasonId,
              seasonName: season.seasonName,
            },
          })),
        )
      }
    }
  }, [season, clubSeasonsInMeet, isVirtual])

  // if the user switches from a virtual meet to a regular meet, remove the out-of-conference
  // teams from the clubs in the meet
  useEffect(() => {
    if (!isVirtual) {
      const clubIdsInMeet = clubSeasonsInMeet.map((cS) => cS.club.clubId)
      const possibleClubIds = availableClubsToAddToMeet.map((aC) => aC.clubId)
      let badClubs = clubIdsInMeet.filter(
        (id) => !possibleClubIds.includes(id) && id !== impersonateClub?.clubId,
      )
      // figure out if there are clubs in the meet that shouldn't be there
      if (badClubs.length > 0) {
        // get an array of the clubs in the meet and a corresponding numeric array of their ids
        const newClubSeasonsInMeet = cloneDeep(clubSeasonsInMeet).filter(
          (cl) => cl.club.clubId !== undefined,
        )
        const newClubIds = newClubSeasonsInMeet.map(
          (nCS) => nCS.club.clubId as number,
        )
        // find the clubIds that specifically should NOT be in the meet but are there
        // these ids need to be removed
        badClubs = badClubs.filter((bC) => bC !== undefined)
        // for each of the clubIds that needs to be removed, go through the existing list of clubs in
        // the meet and splice out any clubSeasons with that clubId
        for (let badClubId of badClubs) {
          let badIdx: number | undefined
          // as long as the badClubId is still in the list of clubIds in the meet, keep removing occurrences
          while ((badIdx = newClubIds.indexOf(badClubId as number)) !== -1) {
            // if we're inside this loop it means we found the bad clubId in the meet
            // this is where we remove it
            newClubSeasonsInMeet.splice(badIdx, 1)
            newClubIds.splice(badIdx, 1)
          }
        }
        // at this point we've gone through every clubId that shouldn't be in the meet and we've removed
        // the corresponding clubs. now let's update our state
        setClubSeasonsInMeet(newClubSeasonsInMeet)
      }
    }
  }, [
    impersonateClub?.clubId,
    clubSeasonsInMeet,
    availableClubsToAddToMeet,
    isVirtual,
  ])

  const getAvailableEventOrdersForClub = useCallback(
    (seasonId: number) => {
      const getLeagueEventOrders = swimminglyApi
        .get(gen(`/api/getLeagueEventOrdersForSeason/${seasonId}?hasEvents=1`))
        .catch((err) => {
          message.error("Problem finding league event orders")
          return []
        })
      const getClubEventOrders = swimminglyApi
        .post(gen(`/api/getClubEventOrders`))
        .body({ clubId: impersonateClub?.clubId, hasEvents: true })
        .catch((err) => {
          message.error("Problem finding club event orders")
          return []
        })
      Promise.all([getLeagueEventOrders, getClubEventOrders])
        .then(([leagueData, clubData]: any[]) => {
          const leagueEventOrders: {
            eventOrderId: number
            name: string
          }[] = []
          const clubEventOrders: {
            eventOrderId: number
            name: string
          }[] = []
          if (leagueData.status === "success") {
            leagueEventOrders.push(...leagueData.eventOrderArray.map((evtOrd: any) => ({
              eventOrderId: evtOrd.id,
              name: evtOrd.name,
            })))
          }
          if (clubData.status === "success") {
            clubEventOrders.push(...clubData.eventOrderArray.map((evtOrd: any) => ({
              eventOrderId: evtOrd.id,
              name: evtOrd.name,
            })))
          }
          setAvailableLeagueEventOrders(leagueEventOrders)
          setAvailableClubEventOrders(clubEventOrders)
          return [...leagueEventOrders, ...clubEventOrders]
        })
        .then(setAvailableEventOrders)
    },
    [impersonateClub],
  )

  // get all of the available event orders based on the aliased club and season
  useEffect(() => {
    let seasonId = season?.seasonId
    if (seasonId) {
      getAvailableEventOrdersForClub(seasonId)
    }
  }, [getAvailableEventOrdersForClub, season?.seasonId])

  // Eagerly fetch event order preview when event order changes (not just when modal opens).
  // Also extracts distinct session numbers and clears per-session event assignments.
  useEffect(() => {
    if (!eventOrder?.eventOrderId) {
      setEventOrderPreviewEvents([])
      setEventOrderPreviewName(null)
      setEventOrderSessionNumbers([])
      setSessionEventAssignments({})
      return
    }

    let mounted = true
    setEventOrderPreviewLoading(true)

    swimminglyApi
      .get(gen(`/api/getEventOrderPreview/${eventOrder.eventOrderId}`))
      .then(
        (data: {
          status?: string
          eventOrderName?: string
          meetEvents?: TMeetEvent[]
          message?: string
        }) => {
          if (!mounted) return
          if (data?.status !== "success") {
            throw new Error(data?.message || "Problem loading event order preview.")
          }
          const events = data.meetEvents || []
          setEventOrderPreviewName(data.eventOrderName || eventOrder.name)
          setEventOrderPreviewEvents(events)
          // Extract distinct session numbers
          const seen = new Set<number>()
          const nums = events
            .map((e) => e.sessionNumber)
            .filter((n): n is number => n != null)
            .filter((n) => { if (seen.has(n)) return false; seen.add(n); return true })
            .sort((a, b) => a - b)
          setEventOrderSessionNumbers(nums)
          setSessionEventAssignments({})
        },
      )
      .catch((err) => {
        console.error(err)
        if (mounted) {
          setEventOrderPreviewName(eventOrder.name)
          setEventOrderPreviewEvents([])
          setEventOrderSessionNumbers([])
          message.error("Problem loading event order preview.")
        }
      })
      .finally(() => {
        if (mounted) setEventOrderPreviewLoading(false)
      })

    return () => { mounted = false }
  }, [eventOrder?.eventOrderId, eventOrder?.name])

  useEffect(() => {
    setEventOrderPreviewOpened(false)
    setReviewSectionsConfirmed((current) => ({
      ...current,
      eventOrder: false,
    }))
  }, [eventOrder?.eventOrderId])

  // Auto-assign session numbers to championship sessions by position whenever
  // the event order changes and session numbers are detected.
  useEffect(() => {
    if (meetTypeChoice !== "multiSession" || eventOrderSessionNumbers.length === 0) return
    setSessions((prev) =>
      prev.map((s, i) => ({
        ...s,
        sessionNumber: eventOrderSessionNumbers[i] ?? null,
      }))
    )
  }, [eventOrderSessionNumbers, meetTypeChoice])

  // Championship meets are always in-person — reset isVirtual if user previously
  // had virtual selected and then switched to championship.
  useEffect(() => {
    if (meetTypeChoice === "multiSession") {
      setIsVirtual(false)
    }
  }, [meetTypeChoice])

  const setReviewSectionConfirmed = useCallback(
    (section: keyof IReviewSectionsState, confirmed: boolean) => {
      setReviewSectionsConfirmed((current) => ({
        ...current,
        [section]: confirmed,
      }))
    },
    [],
  )

  const openEventOrderPreview = useCallback(() => {
    setEventOrderPreviewOpened(true)
    setReviewSectionConfirmed("eventOrder", true)
    setEventOrderPreviewVisible(true)
  }, [setReviewSectionConfirmed])

  const resetState = useCallback(() => {
    setIsVirtual(false)
    setLoading(false)
    setCreateMeetStep("inperson-virtual")
    setMeetTypeChoice(null)
    setChampionshipName("")
    setSessions([])
    setEnableDivisions(false)
    setFinalsConfig({ bFinalEnabled: true, cFinalEnabled: false, dFinalEnabled: false, direction: "slowestFirst" })
    setDivisions([])
    setDivisionInput("")
    setClubDivisionAssignments({})
    setVirtualClubsModalVisible(false)
    setVirtualDraftClubSeasonsInMeet([])
    nextVirtualTempClubIdRef.current = -1
    // clear selected dates on reset so user must explicitly pick them
    setMeetStartDate(null)
    setMeetEndDate(null)
    setMeetEntriesDeadlineEnabled(false)
    setMeetEntriesDeadline(null)
    setAutoMarkHeatSheetReadyAfterDeadline(false)
    setMeetEntriesDeadlineInfoVisible(false)
    setMeetName("")
    setEventOrder(null)
    setEventOrderInfoVisible(false)
    setMeetTypeInfoVisible(false)
    setTeamDivisionsInfoVisible(false)
    setAssignEventsModalOpen(false)
    setReviewSectionsConfirmed(DEFAULT_REVIEW_SECTIONS_STATE)
    setEventOrderPreviewOpened(false)
    setEventOrderPreviewVisible(false)
    setEventOrderPreviewLoading(false)
    setEventOrderPreviewName(null)
    setEventOrderPreviewEvents([])
    setEventOrderSessionNumbers([])
    setSessionEventAssignments({})
    setClubSeasonsInMeet(
      impersonateClub ? [{ club: impersonateClub, season: null }] : [],
    )
    setHomeClub(impersonateClub?.clubId || null)
    setHasIndividualEntryLimit(true)
    setIndividualEntryLimit(3)
    setHasRelayEntryLimit(true)
    setRelayEntryLimit(2)
    setHasTotalEntryLimit(false)
    setTotalEntryLimit(null)
    setPool(null)
    setPoolPickerVisible(false)
    setPoolPickerClubId(null)
    setSamePoolAllSessions(true)
    setSessionPools({})
    setPoolPickerTargetSessionId(null)
    setIndividualScoringSettings([
      { place: 1, points: 5 },
      { place: 2, points: 3 },
      { place: 3, points: 1 },
    ])
    setIndividualScoringLimit(5)
    setHasIndividualScoringLimit(true)
    setRelayScoringSettings([{ place: 1, points: 5 }])
    setRelayScoringLimit(5)
    setHasRelayScoringLimit(true)
  }, [impersonateClub])

  // reset everything when the modal is opened or closed
  useEffect(resetState, [resetState, visible])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const mediaQuery = window.matchMedia("(max-width: 750px)")
    const handleViewportChange = (event: MediaQueryList | MediaQueryListEvent) => {
      setIsMobileViewport(event.matches)
    }

    handleViewportChange(mediaQuery)

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleViewportChange)
      return () => mediaQuery.removeEventListener("change", handleViewportChange)
    }

    mediaQuery.addListener(handleViewportChange)
    return () => mediaQuery.removeListener(handleViewportChange)
  }, [])

  useEffect(() => {
    if (!visible) {
      return
    }

    const popupBody = document.querySelector(
      ".create-meet-modal .ant-modal-body, .create-meet-modal .ant-drawer-body",
    )
    if (popupBody instanceof HTMLElement) {
      popupBody.scrollTo({ top: 0, behavior: "auto" })
    }
  }, [createMeetStep, visible])

  const validateMeetConfiguration = useCallback(() => {
    if (loading) {
      return false
    }

    if (!impersonateClub) {
      message.error("You have to be aliased to a club to create a meet")
      return false
    }

    if (meetTypeChoice === "multiSession") {
      if (!championshipName || championshipName.trim().length < 2) {
        message.error("Please enter a meet series name (at least 2 characters)")
        return false
      }
      if (sessions.length === 0) {
        message.error("Please add at least one session")
        return false
      }
      const missingDate = sessions.findIndex((s) => !s.date)
      if (missingDate !== -1) {
        message.error(`Session ${missingDate + 1} is missing a date`)
        return false
      }
      if (eventOrderPreviewLoading) {
        message.error("Please wait for the event order to finish loading")
        return false
      }
      if (eventOrderPreviewEvents.length > 0) {
        // Finals sessions are auto-mirrored from prelims — skip them in validation
        const unassignedIdx = sessions.findIndex(
          (s) => s.type !== "finals" && (!sessionEventAssignments[s.id] || sessionEventAssignments[s.id].length === 0),
        )
        if (unassignedIdx !== -1) {
          message.error(
            `Session ${unassignedIdx + 1} has no events assigned. Every session must have at least one event.`,
          )
          return false
        }
      }
    } else {
      if (!meetName || meetName.trim() === "") {
        message.error("Please enter a valid meet name")
        return false
      }
      if (meetName.trim().length < 2) {
        message.error("Please enter a longer meet name")
        return false
      }
    }
    if (!eventOrder) {
      message.error("Must choose event order")
      return false
    }
    if (meetTypeChoice !== "multiSession" && !meetStartDate) {
      message.error(isVirtual ? "Please pick meet window start date" : "Please pick a meet date")
      return false
    }
    if (isVirtual && !meetEndDate) {
      message.error("Please pick meet window end date")
      return false
    }

    const selectedClubIds = clubSeasonsInMeet
      .map((clubSeason) => clubSeason.club.clubId)
      .filter((clubId): clubId is number => typeof clubId === "number" && clubId > 0)

    if (selectedClubIds.length === 0) {
      message.error("Please add at least one participating club")
      return false
    }
    if (isVirtual && selectedClubIds.length !== clubSeasonsInMeet.length) {
      message.error("Please select each participating club before creating the virtual meet")
      return false
    }

    let validClubSeasons = true
    clubSeasonsInMeet.forEach((clubSeason) => {
      if (!clubSeason.season?.seasonId) {
        validClubSeasons = false
      }
    })
    if (!validClubSeasons && !isVirtual) {
      message.error(
        "It looks like you haven't tagged this meet to a swim season.",
      )
      return false
    }
    if (!isVirtual && !pool && !(meetTypeChoice === "multiSession" && !samePoolAllSessions)) {
      message.error("Please choose a home pool for this meet")
      return false
    }
    if (meetTypeChoice === "multiSession" && !samePoolAllSessions) {
      const missingIdx = sessions.findIndex((s) => !sessionPools[s.id])
      if (missingIdx !== -1) {
        message.error(`Session ${missingIdx + 1} is missing a pool selection`)
        return false
      }
    }
    if (!isVirtual && meetEntriesDeadlineEnabled && meetEntriesDeadline) {
      if (meetEntriesDeadline.isBefore(dayjs().startOf("day"))) {
        message.error("Deadline cannot be in the past")
        return false
      }

      if (meetEntriesDeadline.isAfter(meetStartDate)) {
        message.error("Meet entries deadline cannot be after the meet start time")
        return false
      }
    }

    return true
  }, [
    championshipName,
    clubSeasonsInMeet,
    eventOrder,
    eventOrderPreviewEvents,
    eventOrderPreviewLoading,
    sessionEventAssignments,
    sessions,
    impersonateClub,
    isVirtual,
    loading,
    meetEndDate,
    meetEntriesDeadline,
    meetEntriesDeadlineEnabled,
    meetName,
    meetStartDate,
    meetTypeChoice,
    pool,
    samePoolAllSessions,
    sessionPools,
  ])

  const openReviewStep = useCallback(() => {
    if (!validateMeetConfiguration()) {
      return
    }

    setReviewSectionsConfirmed(DEFAULT_REVIEW_SECTIONS_STATE)
    setEventOrderPreviewOpened(false)
    setCreateMeetStep("review")
  }, [validateMeetConfiguration])

  const allReviewSectionsConfirmed = Object.entries(reviewSectionsConfirmed).every(
    ([key, confirmed]) => {
      if (key === "championshipConfig" && meetTypeChoice !== "multiSession") return true
      if (key === "entryLimits" && meetTypeChoice === "multiSession") return true
      if (key === "eventOrder" && meetTypeChoice === "multiSession") return true
      return confirmed
    },
  )

  const selectedClubSummaries = clubSeasonsInMeet.map((clubSeason) => {
    const clubName = getClubDisplayName(clubSeason.club)
    const seasonName = clubSeason.season?.seasonName || season?.seasonName || "No season selected"

    return {
      key: `${clubSeason.club.clubId || clubName}_${seasonName}`,
      clubName,
      clubId: clubSeason.club.clubId,
      seasonName,
      isHomeClub: homeClub != null && clubSeason.club.clubId === homeClub,
    }
  })

  const inPersonReviewSeasonLabel = !isVirtual
    ? (() => {
        const uniqueSeasonNames = Array.from(
          new Set(selectedClubSummaries.map((club) => club.seasonName).filter(Boolean)),
        )

        if (uniqueSeasonNames.length === 0) return null
        if (uniqueSeasonNames.length === 1) return uniqueSeasonNames[0]
        return "Multiple seasons"
      })()
    : null

  const meetSummaryItems = [
    meetTypeChoice !== "multiSession"
      ? {
          label: isVirtual ? "Meet window" : "Meet date",
          value: isVirtual
            ? `${meetStartDate?.format("MMM D, YYYY") || "Not set"} - ${meetEndDate?.format("MMM D, YYYY") || "Not set"}`
            : meetStartDate?.format("dddd, MMMM D, YYYY") || "Not set",
        }
      : null,
    meetTypeChoice !== "multiSession"
      ? {
          label: isVirtual ? "Results course" : "Start time",
          value: isVirtual
            ? virtualCourse
            : meetStartDate?.format("h:mma") || "Not set",
        }
      : null,
    {
      label: "Meet type",
      value: isVirtual ? "Virtual swim meet" : "In-person swim meet",
    },
    {
      label: isVirtual ? "Invited clubs" : "Host club",
      value: isVirtual
        ? `${selectedClubSummaries.length} club${selectedClubSummaries.length === 1 ? "" : "s"}`
        : selectedClubSummaries.find((club) => club.isHomeClub)?.clubName || "Not set",
    },
    !isVirtual && !(meetTypeChoice === "multiSession" && !samePoolAllSessions)
      ? {
          label: "Meet pool",
          value: pool?.poolName || "Not set",
        }
      : null,
    !isVirtual && !(meetTypeChoice === "multiSession" && !samePoolAllSessions)
      ? {
          label: "Pool address",
          value: pool?.address || "Not set",
        }
      : null,
  ].filter(Boolean) as { label: string; value: string }[]

  const entryAndScoringItems = [
    {
      label: "Individual entry limit",
      value: hasIndividualEntryLimit ? `${individualEntryLimit} per swimmer` : "No limit",
    },
    {
      label: "Relay entry limit",
      value: hasRelayEntryLimit ? `${relayEntryLimit} per swimmer` : "No limit",
    },
    {
      label: "Total entry limit",
      value: hasTotalEntryLimit && totalEntryLimit != null ? `${totalEntryLimit} per swimmer` : "No limit",
    },
    {
      label: "Meet entries deadline",
      value:
        !isVirtual && meetEntriesDeadlineEnabled && meetEntriesDeadline
          ? meetEntriesDeadline.format("MMM D, YYYY h:mma")
          : "Not enabled",
    },
    {
      label: "Individual scoring",
      value: isVirtual
        ? formatScoringSettings(individualScoringSettings)
        : "Set on Scorekeeper at meet-time",
      subtle: !isVirtual,
    },
    {
      label: "Individual scoring limit",
      value: isVirtual
        ? hasIndividualScoringLimit
          ? `${individualScoringLimit} places`
          : "No limit"
        : "Set on Scorekeeper at meet-time",
      subtle: !isVirtual,
    },
    {
      label: "Relay scoring",
      value: isVirtual
        ? formatScoringSettings(relayScoringSettings)
        : "Set on Scorekeeper at meet-time",
      subtle: !isVirtual,
    },
    {
      label: "Relay scoring limit",
      value: isVirtual
        ? hasRelayScoringLimit
          ? `${relayScoringLimit} places`
          : "No limit"
        : "Set on Scorekeeper at meet-time",
      subtle: !isVirtual,
    },
  ]

  const addMeet = async () => {
    if (!validateMeetConfiguration()) {
      return
    }

    const selectedImpersonateClub = impersonateClub!
    const selectedEventOrder = eventOrder!
    // Championship meets have no single start date; use dayjs() as a safe placeholder
    // (createMultiSessionMeet ignores meetDate/startDate — it uses per-session dates)
    const selectedMeetStartDate = meetStartDate ?? dayjs()
    const selectedClubIds = clubSeasonsInMeet
      .map((clubSeason) => clubSeason.club.clubId)
      .filter((clubId): clubId is number => typeof clubId === "number" && clubId > 0)
    const postBody = {
      aliasedClubId: selectedImpersonateClub.clubId,
      autoMarkHeatSheetReadyAfterDeadline: isVirtual
        ? false
        : Boolean(meetEntriesDeadlineEnabled && meetEntriesDeadline) && autoMarkHeatSheetReadyAfterDeadline,
      clubIds: selectedClubIds,
      clubSeasons: clubSeasonsInMeet.map((cS) => ({
        clubId: cS.club.clubId,
        seasonId: cS.season?.seasonId,
      })),
      course: isVirtual ? virtualCourse : pool?.course || "SCY",
      eventOrderId: selectedEventOrder.eventOrderId,
      homeClubId: homeClub,
      individualEntryLimit: hasIndividualEntryLimit
        ? individualEntryLimit
        : null,
      individualScoringArray: individualScoringSettings,
      individualScoringLimit: hasIndividualScoringLimit
        ? individualScoringLimit
        : null,
      meetDate: isVirtual ? meetEndDate!.format("YYYY-MM-DD") : selectedMeetStartDate.format("YYYY-MM-DD"),
      meetEntriesDeadline: isVirtual || !meetEntriesDeadlineEnabled || !meetEntriesDeadline
        ? null
        : toIsoStringUTC(meetEntriesDeadline),
      meetStartDateTimeUtc: isVirtual || !meetStartDate
        ? null
        : toIsoStringUTC(selectedMeetStartDate),
      meetName,
      meetPoolId: pool?.poolId,
      meetTime: isVirtual ? meetEndDate!.format("hh:mma") : selectedMeetStartDate.format("hh:mma"),
      relayEntryLimit: hasRelayEntryLimit ? relayEntryLimit : null,
      totalEntryLimit: hasTotalEntryLimit ? totalEntryLimit : null,
      relayScoringArray: relayScoringSettings,
      relayScoringLimit: hasRelayScoringLimit ? relayScoringLimit : null,
      startDate: selectedMeetStartDate.format("YYYY-MM-DD"),
      createdBy: actingUser?.userId,
      ...getActingUserRequestFields(actingUser),
    }
    const url = isVirtual
      ? gen(`/api/createVirtualMeetShell`)
      : meetTypeChoice === "multiSession"
      ? gen(`/api/createMultiSessionMeet`)
      : gen(`/api/createTraditionalSwimMeet`)

    const championshipExtras =
      meetTypeChoice === "multiSession"
        ? {
            championshipName: championshipName.trim(),
            finalsConfig: sessions.some((s) => s.type === "prelims" || s.type === "finals")
              ? finalsConfig
              : null,
            enableDivisions,
            divisions: enableDivisions ? divisions : [],
            clubDivisionAssignments: enableDivisions && divisions.length > 0
              ? Object.entries(clubDivisionAssignments)
                  .filter(([, divName]) => divName != null)
                  .map(([clubId, divisionName]) => ({ clubId: Number(clubId), divisionName }))
              : [],
            sessions: sessions.map((s) => ({
              type: s.type,
              date: s.date?.format("YYYY-MM-DD"),
              time: s.time ? s.time.format("hh:mma") : null,
              individualEntryLimit: s.hasIndividualEntryLimit ? s.individualEntryLimit : null,
              relayEntryLimit: s.hasRelayEntryLimit ? s.relayEntryLimit : null,
              totalEntryLimit: s.hasTotalEntryLimit ? s.totalEntryLimit : null,
              sessionNumber: s.sessionNumber,
              poolId: samePoolAllSessions
                ? (pool?.poolId ?? null)
                : (sessionPools[s.id]?.poolId ?? null),
              eventIds: (sessionEventAssignments[s.id]?.length ?? 0) > 0
                ? sessionEventAssignments[s.id]
                : null,
            })),
          }
        : {}

    setLoading(true)
    try {
      const data = await swimminglyApi.post(url).body({ ...postBody, ...championshipExtras })

      if (data.status !== "success") {
        message.error(data.message || "Problem creating swim meet")
        return
      }

      if (isVirtual) {
        message.success("Virtual meet successfully created! Finish your club setup next.")
        refreshSchedule?.()
        setOrganizerInvitationIdToRespond(data.organizerInvitationId || null)
        setOrganizerVirtualMeetIdToRespond(data.virtualMeetId || null)
        setOrganizerInvitationSeasonId(season?.seasonId || null)
        setVisible(false)
        if (data.organizerInvitationId) {
          setOrganizerInvitationModalVisible(true)
        }
        return
      }

      message.success("Success!")
      if (season?.clubSeasonId) {
        updateSeasonMeetArrayListener.emit(`updateSeasonMeets${season?.clubSeasonId}`)
      }
      refreshSchedule?.()
      setVisible(false)
    } catch (err) {
      message.error("Problem creating swim meet")
    } finally {
      setLoading(false)
    }
  }

  const shellTitle = (
    <span>
      Add Meet to Season:{" "}
      <b>{season?.seasonName || "NO SEASON SELECTED"}</b>
    </span>
  )

  const shellFooter = createMeetStep === "inperson-virtual"
    ? null
    : createMeetStep === "type-select"
    ? (
        <div key="type-select-footer" className="create-meet-footer create-meet-footer--edit">
          <Button
            key="back"
            type="default"
            shape="round"
            onClick={() => setCreateMeetStep("inperson-virtual")}
          >
            <FontAwesomeIcon icon={faAngleLeft} /> Back
          </Button>
          <Button
            key="cancel"
            type="link"
            shape="round"
            onClick={() => setVisible(false)}
          >
            Cancel
          </Button>
        </div>
      )
    : createMeetStep === "review"
    ? (
        <div
          key="review-footer"
          className="create-meet-footer create-meet-footer--review"
        >
          <Button
            key="back"
            type="default"
            shape="round"
            onClick={() => setCreateMeetStep("edit")}
            disabled={loading}
          >
            Back
          </Button>
          <div className="create-meet-footer-actions">
            <Button
              key="cancel"
              type="link"
              shape="round"
              onClick={() => setVisible(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              key="confirm"
              type="primary"
              shape="round"
              loading={loading}
              disabled={!allReviewSectionsConfirmed}
              onClick={addMeet}
            >
              Confirm & Create Swim Meet
            </Button>
          </div>
        </div>
      )
    : (
        <div key="edit-footer" className="create-meet-footer create-meet-footer--edit">
          <Button
            key="back"
            type="default"
            shape="round"
            onClick={() => {
              if (isVirtual) {
                setIsVirtual(false)
                setMeetTypeChoice(null)
                setCreateMeetStep("inperson-virtual")
              } else {
                setMeetTypeChoice(null)
                setCreateMeetStep("type-select")
              }
            }}
          >
            <FontAwesomeIcon icon={faAngleLeft} /> Back
          </Button>
          <Button
            key="cancel"
            type="link"
            shape="round"
            loading={loading}
            onClick={() => setVisible(false)}
          >
            Cancel
          </Button>{" "}
          <Button
            key="review"
            type="primary"
            shape="round"
            disabled={
              (meetTypeChoice !== "multiSession" && !meetStartDate) ||
              (isVirtual === true && !meetEndDate) ||
              (meetTypeChoice !== "multiSession" && !isVirtual && !pool) ||
              (meetTypeChoice === "multiSession" && (sessions.length === 0 || sessions.some((s) => !s.date || !s.time))) ||
              (meetTypeChoice === "multiSession" && samePoolAllSessions && !pool) ||
              (meetTypeChoice === "multiSession" && !samePoolAllSessions && sessions.some((s) => !sessionPools[s.id])) ||
              (meetTypeChoice === "multiSession" && eventOrderPreviewEvents.length > 0 &&
                (() => {
                  // Count unique event IDs in non-finals sessions (finals auto-mirror prelims)
                  const assignedIds = new Set<number>()
                  sessions.forEach((s) => {
                    if (s.type !== "finals") {
                      ;(sessionEventAssignments[s.id] ?? []).forEach((id) => assignedIds.add(id))
                    }
                  })
                  return assignedIds.size < eventOrderPreviewEvents.length
                })())
            }
            onClick={openReviewStep}
          >
            Review Swim Meet <FontAwesomeIcon icon={faAngleRight} />
          </Button>
        </div>
      )

  const ShellComponent = (isMobileViewport ? StyledMeetDrawer : StyledMeetModal) as any
  const shellProps = isMobileViewport
    ? {
        className: "create-meet-modal",
        rootClassName: "create-meet-drawer-root",
        title: shellTitle,
        open: visible,
        onClose: () => setVisible(false),
        placement: "bottom" as const,
        height: "100dvh",
        closable: { placement: "end" as const },
        footer: shellFooter,
      }
    : {
        className: "create-meet-modal",
        title: shellTitle,
        width: "100%",
        open: visible,
        onCancel: () => setVisible(false),
        confirmLoading: loading,
        footer: shellFooter,
      }

  return (
    <Fragment>
    <ShellComponent {...shellProps}>
    {createMeetStep === "inperson-virtual" ? (
      <div className="create-meet-type-select" style={{ padding: "48px 32px" }}>
        <Typography.Title level={3} style={{ textAlign: "center", marginBottom: 8 }}>
          What kind of meet are you creating?
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ textAlign: "center", marginBottom: 36, fontSize: 15 }}>
          Choose the format for your swim meet.
        </Typography.Paragraph>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center" }}>
          <div
            style={{
              flex: "1 1 260px",
              maxWidth: 340,
              cursor: "pointer",
              border: `2px solid ${hoveredMeetKind === "inperson" ? "#1677ff" : "#e8e8e8"}`,
              borderRadius: 12,
              padding: "28px 24px",
              background: hoveredMeetKind === "inperson" ? "#f0f7ff" : "#fff",
              transition: "border-color 0.18s, background 0.18s, box-shadow 0.18s",
              boxShadow: hoveredMeetKind === "inperson" ? "0 4px 20px rgba(22,119,255,0.14)" : "0 1px 4px rgba(0,0,0,0.07)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
            onMouseEnter={() => setHoveredMeetKind("inperson")}
            onMouseLeave={() => setHoveredMeetKind(null)}
            onClick={() => { setIsVirtual(false); setCreateMeetStep("type-select") }}
          >
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#e6f4ff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, fontSize: 30, color: "#1677ff" }}>
              <FontAwesomeIcon icon={faPeoplePantsSimple} />
            </div>
            <Typography.Title level={4} style={{ marginBottom: 6, color: "#1677ff" }}>In-Person Swim Meet</Typography.Title>
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 18, fontSize: 13 }}>
              Competition with all teams together at the same pool.
            </Typography.Text>
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8, textAlign: "left" }}>
              {["Regular season or championship meet", "Single or multi-session formats", "Dual meets up to invitationals"].map((bullet) => (
                <div key={bullet} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: "#1677ff", fontSize: 13, lineHeight: 1, flexShrink: 0 }}>✓</span>
                  <Typography.Text style={{ fontSize: 13 }}>{bullet}</Typography.Text>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              flex: "1 1 260px",
              maxWidth: 340,
              cursor: "pointer",
              border: `2px solid ${hoveredMeetKind === "virtual" ? "#722ed1" : "#e8e8e8"}`,
              borderRadius: 12,
              padding: "28px 24px",
              background: hoveredMeetKind === "virtual" ? "#f9f0ff" : "#fff",
              transition: "border-color 0.18s, background 0.18s, box-shadow 0.18s",
              boxShadow: hoveredMeetKind === "virtual" ? "0 4px 20px rgba(114,46,209,0.14)" : "0 1px 4px rgba(0,0,0,0.07)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
            onMouseEnter={() => setHoveredMeetKind("virtual")}
            onMouseLeave={() => setHoveredMeetKind(null)}
            onClick={() => { setIsVirtual(true); setMeetTypeChoice("singleSession"); setCreateMeetStep("edit") }}
          >
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#f9f0ff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, fontSize: 30, color: "#722ed1" }}>
              <FontAwesomeIcon icon={faPeopleArrows} />
            </div>
            <Typography.Title level={4} style={{ marginBottom: 6, color: "#722ed1" }}>Virtual Swim Meet</Typography.Title>
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 18, fontSize: 13 }}>
              Each team competes from their pool — no travel required.
            </Typography.Text>
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8, textAlign: "left" }}>
              {["Swimmers compete from any pool", "No travel required", "Full results and standings"].map((bullet) => (
                <div key={bullet} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ color: "#722ed1", fontSize: 13, lineHeight: 1, flexShrink: 0 }}>✓</span>
                  <Typography.Text style={{ fontSize: 13 }}>{bullet}</Typography.Text>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    ) : createMeetStep === "type-select" ? (
      <div className="create-meet-type-select" style={{ padding: "40px 24px" }}>
        <Typography.Title level={3} style={{ marginBottom: 24 }}>
          Choose your in-person meet format
        </Typography.Title>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Card
            hoverable
            style={{ flex: "1 1 260px", cursor: "pointer", border: "2px solid transparent" }}
            onClick={() => { setMeetTypeChoice("singleSession"); setCreateMeetStep("edit") }}
          >
            <Typography.Title level={4} style={{ marginBottom: 4 }}>
              Single Session Meet
            </Typography.Title>
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
              One session, short and sweet.
            </Typography.Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {["Regular season meet", "Single session championship meet", "Dual meet up to invitationals"].map((ex) => (
                <div key={ex} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "#52c41a", fontSize: 12, lineHeight: 1 }}>✓</span>
                  <Typography.Text style={{ fontSize: 13 }}>{ex}</Typography.Text>
                </div>
              ))}
            </div>
          </Card>
          <Card
            hoverable={!isFeatureLocked(FeatureKey.multiSessionMeet) && !MULTI_SESSION_COMING_SOON}
            style={{
              flex: "1 1 260px",
              cursor: isFeatureLocked(FeatureKey.multiSessionMeet) ? "pointer" : MULTI_SESSION_COMING_SOON ? "default" : "pointer",
              border: "2px solid transparent",
              opacity: isFeatureLocked(FeatureKey.multiSessionMeet) ? 0.65 : 1,
            }}
            onClick={() => {
              if (isFeatureLocked(FeatureKey.multiSessionMeet)) {
                openLockedModal(FeatureKey.multiSessionMeet)
              } else if (MULTI_SESSION_COMING_SOON) {
                message.info("Multi-Session Meets are coming soon — stay tuned!")
              } else {
                setMeetTypeChoice("multiSession")
                setCreateMeetStep("edit")
              }
            }}
          >
            <Typography.Title level={4} style={{ marginBottom: 4 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: "inherit" }}>
                Multi-Session Meet
                {isFeatureLocked(FeatureKey.multiSessionMeet) ? (
                  <LockOutlined style={{ fontSize: 13, color: "#aaa", marginLeft: 2 }} />
                ) : MULTI_SESSION_COMING_SOON ? (
                  <span style={{ padding: "0 8px", borderRadius: 10, fontSize: 10, fontWeight: 600, backgroundColor: "#faad14", color: "white", lineHeight: "18px", flexShrink: 0 }}>Coming Soon!</span>
                ) : (
                  <span style={{ padding: "0 6px", borderRadius: 10, fontSize: 10, fontWeight: 600, backgroundColor: "#52c41a", color: "white", lineHeight: "16px", flexShrink: 0 }}>NEW</span>
                )}
              </span>
            </Typography.Title>
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
              Multi-session swim meet across one or multiple days. Supports scoring by team divisions.
            </Typography.Text>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {["Multi-session or multi-day timed finals meet", "Multi-session or multi-day prelims/finals meet"].map((ex) => (
                <div key={ex} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "#722ed1", fontSize: 12, lineHeight: 1 }}>✓</span>
                  <Typography.Text style={{ fontSize: 13 }}>{ex}</Typography.Text>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    ) : createMeetStep === "edit" ? (
    <Fragment>
      <div className="edit-step-shell">
        <div className="edit-step-hero">
          <div>
            <Typography.Title level={3} style={{ marginBottom: 8 }}>
              {meetTypeChoice === "multiSession" ? "Build Your Multi-Session Meet" : "Build Your Swim Meet"}
            </Typography.Title>
            <Typography.Paragraph
              className={`edit-step-meet-name-preview${(meetTypeChoice === "multiSession" ? championshipName : meetName).trim() ? "" : " is-empty"}`}
            >
              {meetTypeChoice === "multiSession"
                ? (championshipName.trim() ? championshipName.trim() : "Meet series name will appear here")
                : (meetName.trim() || "Your meet name will appear here")}
            </Typography.Paragraph>
            <Typography.Paragraph className="edit-section-note">
              Setup your swim meet & proceed to review.
            </Typography.Paragraph>
          </div>
          <Space size={[8, 8]} wrap>
            <Tag color={isVirtual ? "geekblue" : "blue"}>
              {isVirtual ? "Virtual Swim Meet" : "In-Person Swim Meet"}
            </Tag>
            {meetTypeChoice === "multiSession" && <Tag color="purple">Multi-Session</Tag>}
            {eventOrder?.name ? <Tag color="gold">{eventOrder.name}</Tag> : <Tag>Select an event order</Tag>}
            <Tag>{selectedClubSummaries.length} participating club{selectedClubSummaries.length === 1 ? "" : "s"}</Tag>
          </Space>
        </div>

        <div className="edit-step-grid">
          {meetTypeChoice === "multiSession" && (
            <Card
              className="edit-step-card"
              title={
                <div className="edit-step-card-header-input">
                  <span className="edit-field-label">Multi-Session Meet Setup</span>
                  <Input
                    size="large"
                    placeholder="Enter multi-session meet name (e.g. League Championships)…"
                    value={championshipName}
                    onChange={(e) => setChampionshipName(e.target.value)}
                  />
                </div>
              }
            >
              {/* ── Event Order ── */}
              <div style={{ marginBottom: 20 }}>
                <div className="edit-field-label-row" style={{ marginBottom: 6 }}>
                  <span className="edit-field-label" style={{ fontSize: 13 }}>Event Order</span>
                  <Tooltip title="About event orders">
                    <Button
                      type="text"
                      className="edit-field-label-info"
                      aria-label="Learn about event orders"
                      icon={<InfoCircleOutlined />}
                      onClick={() => setEventOrderInfoVisible(true)}
                    />
                  </Tooltip>
                </div>
                <div className="edit-event-order-row">
                  <Select
                    id="event-order-input-championship"
                    size="large"
                    style={{ width: "100%" }}
                    placeholder="Choose an event order"
                    value={eventOrder?.eventOrderId}
                    onChange={(selectedId) => {
                      const selectedEventOrder = availableEventOrders.find(
                        (availableOrder) => availableOrder.eventOrderId === selectedId,
                      )
                      setEventOrder(selectedEventOrder || null)
                    }}
                    popupRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: "4px 0" }} />
                        <div
                          style={{ padding: "4px 12px 8px", cursor: "pointer" }}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setImportEventOrderModalVisible(true)}
                        >
                          <span style={{ color: "#1677ff", fontSize: 13 }}>
                            + Import from EV3/HYV File…
                          </span>
                        </div>
                      </>
                    )}
                  >
                    {availableLeagueEventOrders.length > 0 ? (
                      <Select.OptGroup label="League Event Orders">
                        {availableLeagueEventOrders.map((availableOrder) => (
                          <Select.Option
                            key={`league_event_order_champ_${availableOrder.eventOrderId}`}
                            value={availableOrder.eventOrderId}
                          >
                            {availableOrder.name}
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    ) : null}
                    {availableClubEventOrders.length > 0 ? (
                      <Select.OptGroup label="My Club Event Orders">
                        {availableClubEventOrders.map((availableOrder) => (
                          <Select.Option
                            key={`club_event_order_champ_${availableOrder.eventOrderId}`}
                            value={availableOrder.eventOrderId}
                          >
                            {availableOrder.name}
                          </Select.Option>
                        ))}
                      </Select.OptGroup>
                    ) : null}
                  </Select>
                  <Tooltip title={eventOrder ? "Preview event order" : "Choose an event order first"}>
                    <Button
                      type="default"
                      size="large"
                      disabled={!eventOrder}
                      aria-label="Preview selected event order"
                      icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        openEventOrderPreview()
                      }}
                    />
                  </Tooltip>
                </div>
                {eventOrderPreviewLoading && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>Loading event order…</Typography.Text>
                )}
                {!eventOrderPreviewLoading && eventOrder && (
                  <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                    {eventOrderPreviewEvents.length > 0
                      ? `${eventOrderPreviewEvents.length} events loaded`
                      : "No events found for this event order"}
                  </Typography.Text>
                )}
              </div>

              {/* ── Pool & Venue ── */}
              <div style={{ marginBottom: 16 }}>
                <Divider style={{ margin: "8px 0 12px" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                  <Typography.Text strong>Pool &amp; Venue</Typography.Text>
                  <Switch
                    size="small"
                    checked={samePoolAllSessions}
                    onChange={(checked) => {
                      setSamePoolAllSessions(checked)
                      if (!checked) {
                        // Seed each session with the current global pool when switching to per-session
                        const seeded: Record<string, IMeetPool | null> = {}
                        sessions.forEach((s) => { seeded[s.id] = pool })
                        setSessionPools(seeded)
                      }
                    }}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {samePoolAllSessions ? "Same pool for all sessions" : "Different pool per session"}
                  </Typography.Text>
                </div>
                {samePoolAllSessions && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Button size="small" onClick={openPoolPicker}>
                      {pool ? "Change Pool" : "Select Pool"}
                    </Button>
                    {pool ? (
                      <Typography.Text style={{ fontSize: 13 }}>{pool.poolName}</Typography.Text>
                    ) : (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>No pool selected</Typography.Text>
                    )}
                  </div>
                )}
                {!samePoolAllSessions && sessions.length === 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Add sessions below to assign pools per session.
                  </Typography.Text>
                )}
              </div>

              {/* ── Team Divisions ── */}
              <div>
                <Divider style={{ margin: "8px 0 12px" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <Space size={4} align="center">
                    <Typography.Text strong>Team Divisions</Typography.Text>
                    <Tooltip title="About team divisions">
                      <Button
                        type="text"
                        size="small"
                        icon={<InfoCircleOutlined />}
                        onClick={() => setTeamDivisionsInfoVisible(true)}
                        style={{ padding: 0, height: "auto", minWidth: 0 }}
                      />
                    </Tooltip>
                  </Space>
                  <Switch
                    checked={enableDivisions}
                    onChange={setEnableDivisions}
                    size="small"
                  />
                </div>
                {enableDivisions && (
                  <>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                      Add division names (e.g. "Gold", "Silver") for multi-division team scoring.
                    </Typography.Text>
                    <Space.Compact style={{ width: "100%" }}>
                      <Input
                        placeholder="Division name…"
                        value={divisionInput}
                        onChange={(e) => setDivisionInput(e.target.value)}
                        onPressEnter={() => {
                          const trimmed = divisionInput.trim()
                          if (trimmed && !divisions.includes(trimmed)) {
                            setDivisions((d) => [...d, trimmed])
                            setDivisionInput("")
                          }
                        }}
                      />
                      <Button
                        type="primary"
                        onClick={() => {
                          const trimmed = divisionInput.trim()
                          if (trimmed && !divisions.includes(trimmed)) {
                            setDivisions((d) => [...d, trimmed])
                            setDivisionInput("")
                          }
                        }}
                      >
                        Add
                      </Button>
                    </Space.Compact>
                    {divisions.length > 0 && (
                      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {divisions.map((div) => (
                          <Tag
                            key={div}
                            closable
                            onClose={() => setDivisions((d) => d.filter((x) => x !== div))}
                          >
                            {div}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>


            </Card>
          )}

          <Card
            className="edit-step-card"
            title={isVirtual ? "Participating Clubs" : "Participating Clubs & Venue"}
            extra={
              isVirtual ? (
                <Button type="primary" shape="round" onClick={openVirtualClubsManager}>
                  Add Clubs
                </Button>
              ) : undefined
            }
          >
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
              {isVirtual ? (
                season?.seasonId ? (
                  <VirtualClubsSummary
                    clubSeasonsInMeet={clubSeasonsInMeet}
                  />
                ) : null
              ) : (
                <Fragment>
                  {season?.seasonId ? (
                    <RegularClubsInMeetDisplay
                      clubSeasonsInMeet={clubSeasonsInMeet}
                      availableClubs={availableClubsToAddToMeet}
                      seasonId={season.seasonId}
                      seasonName={season.seasonName}
                      homeClub={homeClub}
                      pool={pool}
                      onOpenPoolPicker={openPoolPicker}
                      hidePool={meetTypeChoice === "multiSession"}
                      divisionOptions={meetTypeChoice === "multiSession" && enableDivisions ? divisions : []}
                      clubDivisionAssignments={clubDivisionAssignments}
                      onClubDivisionChange={(clubId, divisionName) =>
                        setClubDivisionAssignments((prev) => ({ ...prev, [clubId]: divisionName }))
                      }
                    />
                  ) : null}
                </Fragment>
              )}

              {isVirtual ? (
                <div className="edit-field-stack">
                  <span className="edit-field-label">Virtual Results Course</span>
                  <Select
                    size="large"
                    value={virtualCourse}
                    onChange={(value) => setVirtualCourse(value as "SCY" | "SCM" | "LCM")}
                    options={[
                      { value: "SCY", label: "SCY" },
                      { value: "SCM", label: "SCM" },
                      { value: "LCM", label: "LCM" },
                    ]}
                  />
                </div>
              ) : null}
            </Space>
          </Card>

          {meetTypeChoice === "multiSession" && (
            <Card className="edit-step-card" title="Meet Sessions">
              {sessions.length === 0 && (
                  <Typography.Text type="secondary" style={{ fontSize: 13, display: "block", padding: "8px 0 4px" }}>
                    No sessions added yet.{!eventOrder && " Select an event order above first."}
                  </Typography.Text>
                )}

                {sessions.map((session, idx) => {
                  const typeLabel = session.type === "timedFinals" ? "Timed Finals" : session.type === "prelims" ? "Prelims" : "Finals"
                  const typeColor = session.type === "timedFinals" ? "blue" : session.type === "prelims" ? "orange" : "purple"
                  const isFinals = session.type === "finals"
                  const updateSession = (patch: Partial<ChampionshipSession>) => {
                    setSessions((prev) => prev.map((s) => s.id === session.id ? { ...s, ...patch } : s))
                  }
                  const SESSION_COLORS = ["#1677ff", "#52c41a", "#fa8c16", "#722ed1", "#eb2f96", "#13c2c2"]
                  const sessionColor = SESSION_COLORS[idx % SESSION_COLORS.length]
                  return (
                    <div
                      key={session.id}
                      style={{
                        border: "1px solid #e0e0e0",
                        borderLeft: `4px solid ${sessionColor}`,
                        borderRadius: 6,
                        marginBottom: 10,
                        background: idx % 2 === 0 ? "#fff" : "#fafbff",
                        overflow: "hidden",
                      }}
                    >
                      {/* Colored session header band */}
                      <div style={{
                        background: `${sessionColor}18`,
                        borderBottom: `1px solid ${sessionColor}30`,
                        padding: "7px 12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}>
                        <Space size={6}>
                          <Tag
                            color={sessionColor}
                            style={{ fontWeight: 700, marginRight: 0, fontSize: 12 }}
                          >
                            Session {idx + 1}
                          </Tag>
                          <Tag style={{ color: sessionColor, borderColor: `${sessionColor}60`, background: "transparent", fontSize: 12 }}>
                            {typeLabel}
                          </Tag>
                          {isFinals && (
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                              Auto-paired with prelims above
                            </Typography.Text>
                          )}
                          {(sessionEventAssignments[session.id]?.length ?? 0) > 0 && (
                            <Tag style={{ fontSize: 11, marginRight: 0 }}>
                              {sessionEventAssignments[session.id].length} events
                            </Tag>
                          )}
                        </Space>
                        <Button
                          type="text"
                          danger
                          size="small"
                          onClick={() => {
                            if (session.type === "prelims") {
                              setSessions((prev) => {
                                const pIdx = prev.findIndex((s) => s.id === session.id)
                                const toRemove = new Set([session.id])
                                if (pIdx !== -1 && prev[pIdx + 1]?.type === "finals") {
                                  toRemove.add(prev[pIdx + 1].id)
                                }
                                setSessionEventAssignments((prev) => {
                                  const next = { ...prev }
                                  toRemove.forEach((id) => { delete next[id] })
                                  return next
                                })
                                return prev.filter((s) => !toRemove.has(s.id))
                              })
                            } else {
                              setSessionEventAssignments((prev) => {
                                const next = { ...prev }
                                delete next[session.id]
                                return next
                              })
                              setSessions((prev) => prev.filter((s) => s.id !== session.id))
                            }
                          }}
                          icon={<FontAwesomeIcon icon={faTrash} />}
                        />
                      </div>

                      <div style={{ padding: "12px 14px" }}>
                      <Row gutter={[12, 8]}>
                        <Col xs={24} sm={12}>
                          <div className="edit-field-stack">
                            <span className="edit-field-label" style={{ fontSize: 12 }}>Date</span>
                            <DatePicker
                              size="middle"
                              style={{ width: "100%" }}
                              value={session.date}
                              onChange={(d) => updateSession({ date: d })}
                              disabledDate={(d) => {
                                if (!d || !season?.endDate || !season?.startDate) return false
                                const md = d as Dayjs
                                if (dayjs(season.endDate).diff(md, "day") < 0) return true
                                if (dayjs(season.startDate).diff(md, "day") > 0) return true
                                return false
                              }}
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="edit-field-stack">
                            <span className="edit-field-label" style={{ fontSize: 12 }}>Start Time</span>
                            <TimePicker
                              size="middle"
                              style={{ width: "100%" }}
                              format="hh:mm a"
                              use12Hours
                              value={session.time}
                              onChange={(t) => updateSession({ time: t as Dayjs | null })}
                            />
                          </div>
                        </Col>
                        {!samePoolAllSessions && (
                          <Col xs={24}>
                            <div className="edit-field-stack">
                              <span className="edit-field-label" style={{ fontSize: 12 }}>Pool</span>
                              <Space size={6} wrap>
                                <Button size="small" onClick={() => openSessionPoolPicker(session.id)}>
                                  {sessionPools[session.id] ? "Change Pool" : "Select Pool"}
                                </Button>
                                {sessionPools[session.id] ? (
                                  <Typography.Text style={{ fontSize: 12 }}>
                                    {sessionPools[session.id]!.poolName}
                                  </Typography.Text>
                                ) : (
                                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                    No pool selected
                                  </Typography.Text>
                                )}
                                {(() => {
                                  if (session.type !== "finals") return null
                                  const pIdx = sessions.slice(0, idx).reverse().findIndex((s) => s.type === "prelims")
                                  const prelimsSession = pIdx >= 0 ? sessions[idx - 1 - pIdx] : null
                                  const prelimsPool = prelimsSession ? (sessionPools[prelimsSession.id] ?? null) : null
                                  if (!prelimsPool) return null
                                  if (sessionPools[session.id]?.poolId === prelimsPool.poolId) return null
                                  return (
                                    <Button
                                      type="link"
                                      size="small"
                                      style={{ padding: 0, fontSize: 11 }}
                                      onClick={() => setSessionPools((prev) => ({ ...prev, [session.id]: prelimsPool }))}
                                    >
                                      Use Prelims pool ({prelimsPool.poolName})
                                    </Button>
                                  )
                                })()}
                              </Space>
                            </div>
                          </Col>
                        )}
                        {!isFinals && (
                          <>
                            <Col xs={24}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Switch
                                  size="small"
                                  checked={session.entryLimitsEnabled}
                                  onChange={(checked) => updateSession({ entryLimitsEnabled: checked })}
                                />
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Entry Limits</Typography.Text>
                              </div>
                            </Col>
                            {session.entryLimitsEnabled && (
                              <>
                                <Col xs={8}>
                                  <div className="edit-field-stack">
                                    <span className="edit-field-label" style={{ fontSize: 11 }}>Individual</span>
                                    <InputNumber
                                      min={1}
                                      size="small"
                                      style={{ width: "100%" }}
                                      disabled={!session.hasIndividualEntryLimit}
                                      value={session.individualEntryLimit}
                                      onChange={(v) => updateSession({ individualEntryLimit: Number(v || 1) })}
                                    />
                                    <Checkbox
                                      style={{ fontSize: 11 }}
                                      checked={!session.hasIndividualEntryLimit}
                                      onChange={(e) => updateSession({ hasIndividualEntryLimit: !e.target.checked })}
                                    >
                                      No limit
                                    </Checkbox>
                                  </div>
                                </Col>
                                <Col xs={8}>
                                  <div className="edit-field-stack">
                                    <span className="edit-field-label" style={{ fontSize: 11 }}>Relay</span>
                                    <InputNumber
                                      min={1}
                                      size="small"
                                      style={{ width: "100%" }}
                                      disabled={!session.hasRelayEntryLimit}
                                      value={session.relayEntryLimit}
                                      onChange={(v) => updateSession({ relayEntryLimit: Number(v || 1) })}
                                    />
                                    <Checkbox
                                      style={{ fontSize: 11 }}
                                      checked={!session.hasRelayEntryLimit}
                                      onChange={(e) => updateSession({ hasRelayEntryLimit: !e.target.checked })}
                                    >
                                      No limit
                                    </Checkbox>
                                  </div>
                                </Col>
                                <Col xs={8}>
                                  <div className="edit-field-stack">
                                    <span className="edit-field-label" style={{ fontSize: 11 }}>Total</span>
                                    <InputNumber
                                      min={1}
                                      size="small"
                                      style={{ width: "100%" }}
                                      disabled={!session.hasTotalEntryLimit}
                                      value={session.totalEntryLimit ?? undefined}
                                      onChange={(v) => updateSession({ totalEntryLimit: v == null ? null : Number(v) })}
                                    />
                                    <Checkbox
                                      style={{ fontSize: 11 }}
                                      checked={!session.hasTotalEntryLimit}
                                      onChange={(e) => updateSession({ hasTotalEntryLimit: !e.target.checked })}
                                    >
                                      No limit
                                    </Checkbox>
                                  </div>
                                </Col>
                              </>
                            )}
                          </>
                        )}
                      </Row>
                      </div>
                    </div>
                  )
                })}

                {/* Add Session buttons — live below the sessions list */}
                <Space size={8} style={{ marginTop: 8 }}>
                  <Tooltip title={!eventOrder ? "Select an event order first" : undefined}>
                    <Button
                      size="small"
                      disabled={!eventOrder}
                      onClick={() => {
                        const newId = `s_${Date.now()}_tf`
                        setSessions((prev) => [...prev, {
                          id: newId,
                          type: "timedFinals",
                          date: null,
                          time: null,
                          entryLimitsEnabled: false,
                          hasIndividualEntryLimit: hasIndividualEntryLimit,
                          individualEntryLimit: individualEntryLimit,
                          hasRelayEntryLimit: hasRelayEntryLimit,
                          relayEntryLimit: relayEntryLimit,
                          hasTotalEntryLimit: hasTotalEntryLimit,
                          totalEntryLimit: totalEntryLimit,
                          sessionNumber: null,
                        }])
                      }}
                    >
                      + Timed Finals
                    </Button>
                  </Tooltip>
                  <Tooltip title={!eventOrder ? "Select an event order first" : undefined}>
                    <Button
                      size="small"
                      disabled={!eventOrder}
                      onClick={() => {
                        const pId = `s_${Date.now()}_p`
                        const fId = `s_${Date.now() + 1}_f`
                        setSessions((prev) => [
                          ...prev,
                          {
                            id: pId,
                            type: "prelims",
                            date: null,
                            time: null,
                            entryLimitsEnabled: false,
                            hasIndividualEntryLimit: hasIndividualEntryLimit,
                            individualEntryLimit: individualEntryLimit,
                            hasRelayEntryLimit: hasRelayEntryLimit,
                            relayEntryLimit: relayEntryLimit,
                            hasTotalEntryLimit: hasTotalEntryLimit,
                            totalEntryLimit: totalEntryLimit,
                            sessionNumber: null,
                          },
                          {
                            id: fId,
                            type: "finals",
                            date: null,
                            time: null,
                            entryLimitsEnabled: false,
                            hasIndividualEntryLimit: false,
                            individualEntryLimit: 0,
                            hasRelayEntryLimit: false,
                            relayEntryLimit: 0,
                            hasTotalEntryLimit: false,
                            totalEntryLimit: null,
                            sessionNumber: null,
                          },
                        ])
                      }}
                    >
                      + Prelims / Finals
                    </Button>
                  </Tooltip>
                </Space>

              {/* ── Event Assignment ── */}
              {eventOrderPreviewEvents.length > 0 && sessions.length >= 1 && (() => {
                const EA_COLORS = SESSION_COLORS_LIST
                const totalEvents = eventOrderPreviewEvents.length
                // Count unique event IDs in non-finals sessions (finals mirror prelims)
                const assignedUniqueIds = new Set<number>()
                sessions.forEach((s) => {
                  if (s.type !== "finals") {
                    ;(sessionEventAssignments[s.id] ?? []).forEach((id) => assignedUniqueIds.add(id))
                  }
                })
                const totalAssigned = assignedUniqueIds.size
                // Build prelims↔finals pairing for label/display use
                const prelimsSessionsForSummary = sessions.filter((s) => s.type === "prelims")
                const finalsSessionsForSummary = sessions.filter((s) => s.type === "finals")
                const finalsToPrelimsIndexMap: Record<string, number> = {}
                finalsSessionsForSummary.forEach((f, i) => {
                  if (prelimsSessionsForSummary[i]) finalsToPrelimsIndexMap[f.id] = sessions.indexOf(prelimsSessionsForSummary[i])
                })
                const allUnassigned = totalAssigned === 0
                return (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <Typography.Text style={{ fontSize: 12, fontWeight: 600 }}>
                        Event Assignment
                        {!allUnassigned && ` — ${totalAssigned} of ${totalEvents} assigned`}
                      </Typography.Text>
                      <Space size={6}>
                        <Button size="small" type="primary" onClick={() => setAssignEventsModalOpen(true)}>
                          Assign Events to Sessions
                        </Button>
                        {!allUnassigned && (
                          <Button 
                            type="link"
                            size="small" 
                            danger onClick={() => setSessionEventAssignments({})}>
                            Clear all
                          </Button>
                        )}
                      </Space>
                    </div>
                    {allUnassigned ? (
                      <div style={{ border: "1px solid #f0f0f0", borderRadius: 4, padding: "14px 12px", background: "#fafafa", textAlign: "center" }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          No events assigned yet. Click <strong>Assign Events to Sessions</strong> to get started.
                        </Typography.Text>
                      </div>
                    ) : (
                      <div style={{ border: "1px solid #e8e8e8", borderRadius: 4, overflow: "hidden" }}>
                        {sessions.map((s, sIdx) => {
                          const sc = EA_COLORS[sIdx % EA_COLORS.length]
                          const tl = s.type === "timedFinals" ? "Timed Finals" : s.type === "prelims" ? "Prelims" : "Finals"
                          const assigned = sessionEventAssignments[s.id] ?? []
                          const isFinalsSummary = s.type === "finals"
                          const pairedPrelimsIdx = isFinalsSummary ? finalsToPrelimsIndexMap[s.id] : undefined
                          // Only accumulate non-finals session lengths for the sequential offset
                          const startIdx = sessions.slice(0, sIdx).reduce(
                            (acc, ss) => acc + (ss.type !== "finals" ? (sessionEventAssignments[ss.id]?.length ?? 0) : 0), 0,
                          )
                          // Finals: filter events by ID (same as paired prelims); others: sequential slice
                          const myEvents = isFinalsSummary
                            ? eventOrderPreviewEvents.filter((e) => assigned.includes(e.swimEventId))
                            : eventOrderPreviewEvents.slice(startIdx, startIdx + assigned.length)
                          const rangeLabel = isFinalsSummary
                            ? (pairedPrelimsIdx !== undefined ? `mirrors Session ${pairedPrelimsIdx + 1} (Prelims)` : "mirrors Prelims")
                            : (assigned.length === 0 ? "no events" : `events ${startIdx + 1}–${startIdx + assigned.length}`)
                          return (
                            <Fragment key={s.id}>
                              {/* Session band header */}
                              <div style={{ background: `${sc}18`, borderLeft: `4px solid ${sc}`, padding: "5px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Space size={4}>
                                  <Tag color={sc} style={{ fontSize: 11, marginRight: 0, fontWeight: 700 }}>Session {sIdx + 1}</Tag>
                                  <Typography.Text style={{ fontSize: 11 }}>{tl}</Typography.Text>
                                  {isFinalsSummary && <Tag icon={<LockOutlined />} style={{ fontSize: 10, color: "#888", borderColor: "#d9d9d9", background: "#fafafa", marginLeft: 2 }}>auto</Tag>}
                                </Space>
                                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                  {rangeLabel}
                                </Typography.Text>
                              </div>
                              {/* Event preview */}
                              <div style={{ paddingLeft: 14, paddingRight: 10, paddingTop: 4, paddingBottom: 4, background: sIdx % 2 === 0 ? "#fff" : "#fdfeff" }}>
                                {assigned.length === 0 ? (
                                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>— no events assigned —</Typography.Text>
                                ) : myEvents.map((evt) => (
                                  <div key={evt.swimEventId} style={{ fontSize: 11, padding: "1px 0", color: "#555" }}>
                                    {resolveEventNumber(evt.displayNumber, evt.eventNumber)}. {evt.ageGroup} {evt.gender} {evt.distance} {evt.stroke}
                                  </div>
                                ))}
                              </div>

                            </Fragment>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* ── Finals Seeding (only if any prelims exists) ── */}
              {sessions.some((s) => s.type === "prelims") && (
                <div style={{ marginBottom: 16 }}>
                  <Divider style={{ margin: "8px 0 12px" }} />
                  <Typography.Text strong style={{ display: "block", marginBottom: 10 }}>Finals Seeding</Typography.Text>
                  <Row gutter={[12, 8]}>
                    <Col xs={24} sm={12}>
                      <div className="edit-field-stack">
                        <span className="edit-field-label">Finals Heat Template</span>
                        <Space orientation="vertical" size={4}>
                          <Checkbox checked disabled>A Final (always included)</Checkbox>
                          <Checkbox
                            checked={finalsConfig.bFinalEnabled}
                            onChange={(e) => setFinalsConfig((c) => ({ ...c, bFinalEnabled: e.target.checked }))}
                          >
                            B Final (Consolation)
                          </Checkbox>
                          <Checkbox
                            checked={finalsConfig.cFinalEnabled}
                            onChange={(e) => setFinalsConfig((c) => ({ ...c, cFinalEnabled: e.target.checked }))}
                          >
                            C Final (Bonus)
                          </Checkbox>
                          <Checkbox
                            checked={finalsConfig.dFinalEnabled}
                            onChange={(e) => setFinalsConfig((c) => ({ ...c, dFinalEnabled: e.target.checked }))}
                          >
                            D Final
                          </Checkbox>
                        </Space>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div className="edit-field-stack">
                        <span className="edit-field-label">Seeding Direction (all finals)</span>
                        <Radio.Group
                          value={finalsConfig.direction}
                          onChange={(e) => setFinalsConfig((c) => ({ ...c, direction: e.target.value }))}
                        >
                          <Space orientation="vertical" size={4}>
                            <Radio value="slowestFirst">Slowest First (Traditional)</Radio>
                            <Radio value="fastestFirst">Fastest First (Championship)</Radio>
                          </Space>
                        </Radio.Group>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

            </Card>
          )}
          {meetTypeChoice !== "multiSession" && (
          <Card
            className="edit-step-card core-setup-card"
            title={
              <div className="edit-step-card-header-input">
                <span className="edit-field-label">Meet Name</span>
                <Input
                  id="meet-name-input"
                  size="large"
                  placeholder="Enter name of swim meet..."
                  value={meetName}
                  onChange={(e) => setMeetName(e.target.value)}
                />
              </div>
            }
          >
            <Row gutter={[{ xs: 12, sm: 16 }, { xs: 12, sm: 16 }]}>
              <Col xs={24}>
                <div className="edit-field-stack">
                  <div className="edit-field-label-row">
                    <span className="edit-field-label">Event Order</span>
                    <Tooltip title="About event orders">
                      <Button
                        type="text"
                        className="edit-field-label-info"
                        aria-label="Learn about event orders"
                        icon={<InfoCircleOutlined />}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          setEventOrderInfoVisible(true)
                        }}
                      />
                    </Tooltip>
                  </div>
                  <div className="edit-event-order-row">
                    <Select
                      id="event-order-input"
                      size="large"
                      style={{ width: "100%" }}
                      placeholder="Choose an event order"
                      value={eventOrder?.eventOrderId}
                      onChange={(selectedId) => {
                        const selectedEventOrder = availableEventOrders.find(
                          (availableOrder) => availableOrder.eventOrderId === selectedId,
                        )
                        setEventOrder(selectedEventOrder || null)
                      }}
                      popupRender={(menu) => (
                        <>
                          {menu}
                          <Divider style={{ margin: "4px 0" }} />
                          <div
                            style={{ padding: "4px 12px 8px", cursor: "pointer" }}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setImportEventOrderModalVisible(true)}
                          >
                            <span style={{ color: "#1677ff", fontSize: 13 }}>
                              + Import from EV3/HYV File…
                            </span>
                          </div>
                        </>
                      )}
                    >
                      {availableLeagueEventOrders.length > 0 ? (
                        <Select.OptGroup label="League Event Orders">
                          {availableLeagueEventOrders.map((availableOrder) => (
                            <Select.Option
                              key={`league_event_order_${availableOrder.eventOrderId}`}
                              value={availableOrder.eventOrderId}
                            >
                              {availableOrder.name}
                            </Select.Option>
                          ))}
                        </Select.OptGroup>
                      ) : null}
                      {availableClubEventOrders.length > 0 ? (
                        <Select.OptGroup label="My Club Event Orders">
                          {availableClubEventOrders.map((availableOrder) => (
                            <Select.Option
                              key={`club_event_order_${availableOrder.eventOrderId}`}
                              value={availableOrder.eventOrderId}
                            >
                              {availableOrder.name}
                            </Select.Option>
                          ))}
                        </Select.OptGroup>
                      ) : null}
                    </Select>
                    <Tooltip title="About event orders">
                      <Button
                        type="default"
                        size="large"
                        className="edit-event-order-info-button"
                        aria-label="Learn about event orders"
                        icon={<InfoCircleOutlined />}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          setEventOrderInfoVisible(true)
                        }}
                      />
                    </Tooltip>
                    <Tooltip title={eventOrder ? "Preview event order" : "Choose an event order first"}>
                      <Button
                        type="default"
                        size="large"
                        disabled={!eventOrder}
                        aria-label="Preview selected event order"
                        icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          openEventOrderPreview()
                        }}
                      />
                    </Tooltip>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
          )}

          <Fragment>

              {meetTypeChoice !== "multiSession" && (
              <Card className="edit-step-card" title={isVirtual ? "Meet Window" : "Meet Date"}>
                <Space orientation="vertical" size={16} style={{ width: "100%" }}>
                  <div className="edit-date-grid">
                    <div className="edit-field-stack">
                      <span className="edit-field-label">{isVirtual ? "Meet Window Starts" : "Meet Date"}</span>
                      <DatePicker
                        style={{ width: "100%" }}
                        size="large"
                        format="MM-DD-YYYY hh:mm a"
                        showTime={{ format: "hh:mm a" }}
                        showNow={false}
                        value={meetStartDate}
                        allowClear={false}
                        disabledDate={(dateToCheck) => {
                          if (!dateToCheck || !season?.endDate || !season?.startDate) return true
                          const modalDate = dateToCheck as Dayjs
                          if (dayjs(season.endDate).diff(modalDate, "day") < 0) return true
                          if (dayjs(season.startDate).diff(modalDate, "day") > 0) return true
                          if (isVirtual && dayjs(meetEndDate).diff(modalDate, "day") < 0) return true
                          return false
                        }}
                        onChange={(newDate) => {
                          if (newDate !== null) {
                            const nextDate = newDate as Dayjs
                            setMeetStartDate(nextDate)
                            if (!isVirtual) setMeetEndDate(nextDate)
                          }
                        }}
                      />
                    </div>
                    {isVirtual ? (
                      <div className="edit-field-stack">
                        <span className="edit-field-label">Meet Window Ends</span>
                        <DatePicker
                          style={{ width: "100%" }}
                          size="large"
                          format="MM-DD-YYYY hh:mm a"
                          showTime={{ format: "hh:mm a" }}
                          showNow={false}
                          value={meetEndDate}
                          allowClear={false}
                          disabledDate={(dateToCheck) => {
                            if (!dateToCheck || !season?.endDate || !season?.startDate) return true
                            const modalDate = dateToCheck as Dayjs
                            if (dayjs(season.endDate).diff(modalDate, "day") < 0) return true
                            if (dayjs(season.startDate).diff(modalDate, "day") > 0) return true
                            if (isVirtual && dayjs(meetStartDate).diff(modalDate, "day") > 0) return true
                            return false
                          }}
                          onChange={(newDate) => {
                            if (newDate !== null) {
                              const nextDate = newDate as Dayjs
                              setMeetEndDate(nextDate)
                              if (!isVirtual) setMeetStartDate(nextDate)
                            }
                          }}
                        />
                      </div>
                    ) : null}
                  </div>

                  {!isVirtual ? (
                    <Fragment>
                      <Divider style={{ margin: 0 }} />
                      <div className="edit-inline-actions edit-inline-actions--deadline">
                        <Typography.Title level={5} style={{ margin: 0 }}>
                          Meet Entries Deadline
                        </Typography.Title>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setMeetEntriesDeadlineInfoVisible(true)}
                          style={{ paddingInline: 4, height: "auto" }}
                          aria-label="About meet entries deadline"
                        >
                          <InfoCircleOutlined />
                        </Button>
                      </div>
                      <Checkbox
                        checked={meetEntriesDeadlineEnabled}
                        disabled={!meetStartDate}
                        onChange={(event) => {
                          const nextEnabled = event.target.checked
                          setMeetEntriesDeadlineEnabled(nextEnabled)
                          if (!nextEnabled) {
                            setMeetEntriesDeadline(null)
                            setAutoMarkHeatSheetReadyAfterDeadline(false)
                          }
                        }}
                      >
                        Enable a meet entries deadline
                      </Checkbox>
                      {!meetStartDate ? (
                        <Typography.Text type="secondary">
                          Select the meet date first to enable this option.
                        </Typography.Text>
                      ) : null}
                      {meetEntriesDeadlineEnabled ? (
                        <div className="edit-date-grid">
                          <div className="edit-field-stack">
                            <span className="edit-field-label">Deadline</span>
                            <DatePicker
                              style={{ width: "100%" }}
                              size="large"
                              format="MM-DD-YYYY hh:mm a"
                              showTime={{ format: "hh:mm a" }}
                              showNow={false}
                              value={meetEntriesDeadline}
                              allowClear
                              disabledDate={(dateToCheck) => {
                                if (!dateToCheck) return false
                                const candidate = dateToCheck as Dayjs
                                if (candidate.endOf("day").isBefore(dayjs().startOf("day"))) {
                                  return true
                                }
                                if (meetStartDate && candidate.startOf("day").isAfter(meetStartDate.endOf("day"))) {
                                  return true
                                }
                                return false
                              }}
                              disabledTime={(selectedDate) => {
                                if (!selectedDate || !meetStartDate || !selectedDate.isSame(meetStartDate, "day")) {
                                  return {}
                                }

                                const maxHour = meetStartDate.hour()
                                const maxMinute = meetStartDate.minute()

                                return {
                                  disabledHours: () =>
                                    Array.from({ length: 24 }, (_, index) => index).filter(
                                      (hour) => hour > maxHour,
                                    ),
                                  disabledMinutes: (selectedHour) =>
                                    selectedHour === maxHour
                                      ? Array.from({ length: 60 }, (_, index) => index).filter(
                                          (minute) => minute > maxMinute,
                                        )
                                      : [],
                                }
                              }}
                              onChange={(newDate) => {
                                const nextValue = newDate as Dayjs | null
                                setMeetEntriesDeadline(nextValue)
                                if (!nextValue) {
                                  setAutoMarkHeatSheetReadyAfterDeadline(false)
                                }
                              }}
                            />
                          </div>
                          <div className="edit-field-stack">
                            <span className="edit-field-label">Post-Deadline Action</span>
                            <Radio.Group
                              value={autoMarkHeatSheetReadyAfterDeadline ? "auto" : "manual"}
                              onChange={(event) =>
                                setAutoMarkHeatSheetReadyAfterDeadline(
                                  event.target.value === "auto",
                                )
                              }
                              disabled={!meetEntriesDeadline}
                            >
                              <Space orientation="vertical" size={6}>
                                <Radio value="auto">Auto-Set Heat Sheet Ready</Radio>
                                <Radio value="manual">Manually Set Heat Sheet Ready</Radio>
                              </Space>
                            </Radio.Group>
                          </div>
                        </div>
                      ) : null}
                      <Alert
                        type="info"
                        showIcon
                        title="This deadline is optional and can be edited later from Meet Actions modal."
                      />
                    </Fragment>
                  ) : null}
                </Space>
              </Card>
              )}

              {meetTypeChoice !== "multiSession" && (
              <Card className="edit-step-card" title={isVirtual ? "Entry Limits & Scoring" : "Entry Limits"}>
                <Space orientation="vertical" size={16} style={{ width: "100%" }}>
                  <div className="edit-limit-grid">
                    <div className="edit-limit-item">
                      <Tooltip title="Maximum number of individual events for a swimmer in this meet.">
                        <Typography.Text strong>Individual Limit</Typography.Text>
                      </Tooltip>
                      <InputNumber
                        min={1}
                        size="large"
                        style={{ width: "100%" }}
                        disabled={!hasIndividualEntryLimit}
                        value={individualEntryLimit}
                        onChange={(value) => setIndividualEntryLimit(Number(value || 1))}
                      />
                      <Checkbox
                        checked={!hasIndividualEntryLimit}
                        onChange={(e) => setHasIndividualEntryLimit(!e.target.checked)}
                      >
                        No limit
                      </Checkbox>
                    </div>
                    <div className="edit-limit-item">
                      <Tooltip title="Maximum number of relay events for a swimmer in this meet.">
                        <Typography.Text strong>Relay Limit</Typography.Text>
                      </Tooltip>
                      <InputNumber
                        min={1}
                        size="large"
                        style={{ width: "100%" }}
                        disabled={!hasRelayEntryLimit}
                        value={relayEntryLimit}
                        onChange={(value) => setRelayEntryLimit(Number(value || 1))}
                      />
                      <Checkbox
                        checked={!hasRelayEntryLimit}
                        onChange={(e) => setHasRelayEntryLimit(!e.target.checked)}
                      >
                        No limit
                      </Checkbox>
                    </div>
                    <div className="edit-limit-item">
                      <Tooltip title="Maximum combined number of official individual and relay events for a swimmer in this meet. Use this when the total allowed swims is less than the sum of the individual and relay limits.">
                        <Typography.Text strong>Total Entry Limit</Typography.Text>
                      </Tooltip>
                      <InputNumber
                        min={1}
                        size="large"
                        style={{ width: "100%" }}
                        disabled={!hasTotalEntryLimit}
                        value={totalEntryLimit ?? undefined}
                        onChange={(value) => setTotalEntryLimit(value == null ? null : Number(value))}
                      />
                      <Checkbox
                        checked={!hasTotalEntryLimit}
                        onChange={(e) => setHasTotalEntryLimit(!e.target.checked)}
                      >
                        No limit
                      </Checkbox>
                    </div>
                  </div>

                  {isVirtual ? (
                    <Fragment>
                      <Divider style={{ margin: 0 }} />
                      <Typography.Title level={5} style={{ margin: 0 }}>
                        Scoring Configuration
                      </Typography.Title>
                      <div className="edit-date-grid">
                        <div className="edit-scoring-panel">
                          <IndividualScoringConfig individualScoring={individualScoringSettings} />
                        </div>
                        <div className="edit-scoring-panel">
                          <RelayScoringConfig relayScoring={relayScoringSettings} />
                        </div>
                      </div>
                      <div className="edit-scoring-limit-grid">
                        <div className="edit-limit-item">
                          <Typography.Text strong>Individual Scoring Limit</Typography.Text>
                          <InputNumber
                            min={1}
                            size="large"
                            style={{ width: "100%" }}
                            disabled={!hasIndividualScoringLimit}
                            value={individualScoringLimit}
                            onChange={(value) => setIndividualScoringLimit(Number(value || 1))}
                          />
                          <Checkbox
                            checked={!hasIndividualScoringLimit}
                            onChange={(e) => setHasIndividualScoringLimit(!e.target.checked)}
                          >
                            No limit
                          </Checkbox>
                        </div>
                        <div className="edit-limit-item">
                          <Typography.Text strong>Relay Scoring Limit</Typography.Text>
                          <InputNumber
                            min={1}
                            size="large"
                            style={{ width: "100%" }}
                            disabled={!hasRelayScoringLimit}
                            value={relayScoringLimit}
                            onChange={(value) => setRelayScoringLimit(Number(value || 1))}
                          />
                          <Checkbox
                            checked={!hasRelayScoringLimit}
                            onChange={(e) => setHasRelayScoringLimit(!e.target.checked)}
                          >
                            No limit
                          </Checkbox>
                        </div>
                      </div>
                    </Fragment>
                  ) : null}
                </Space>
              </Card>
              )}
            </Fragment>
        </div>
      </div>

      <Modal
        title="About Event Orders"
        open={eventOrderInfoVisible}
        onCancel={() => setEventOrderInfoVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setEventOrderInfoVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <p>
          You can choose from league-level event orders here. These are managed by your
          league admins and usually include standard formats such as your primary dual meet
          event order or championship meet event order.
        </p>
        <p>
          You can also choose from club-level event orders. Any club admin on your team can
          manage those under Manage Club. Club-level event orders are useful for custom meet
          formats such as intersquad meets, relay meets, or other hosted formats your club
          wants to run.
        </p>
        <p>
          When you create a swim meet, the selected event order is copied into that meet at
          that moment. You can still edit the original event order later, but those later
          changes do not automatically change event orders for meets that were already created.
        </p>
        <p>
          Only event orders that currently contain one or more events are available to select.
        </p>
      </Modal>

      <Modal
        title="About Team Divisions"
        open={teamDivisionsInfoVisible}
        onCancel={() => setTeamDivisionsInfoVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setTeamDivisionsInfoVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <p>
          <strong>Team Divisions</strong> let you split the participating clubs into named groups for multi-division team scoring — for example <em>Gold</em> and <em>Silver</em>, or <em>Division 1</em> and <em>Division 2</em>.
        </p>
        <p>
          When divisions are enabled, you assign clubs to a division from the Participating Clubs section. Swimmingly then calculates and displays separate team scores for each division, so smaller or lower-seeded clubs aren't competing directly against larger programs in the standings.
        </p>
        <p>
          If you don't need separate scoring groups, leave this off. All clubs will be scored together in a single standings table.
        </p>
      </Modal>

      <AssignEventsToSessionsModal
        open={assignEventsModalOpen}
        sessions={sessions}
        events={eventOrderPreviewEvents}
        sessionEventAssignments={sessionEventAssignments}
        onClose={() => setAssignEventsModalOpen(false)}
        onConfirm={(newAssignments) => {
          setSessionEventAssignments(newAssignments)
          setAssignEventsModalOpen(false)
        }}
      />

      <Modal
        title="Meet Types"
        open={meetTypeInfoVisible}
        onCancel={() => setMeetTypeInfoVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setMeetTypeInfoVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <p>
          <strong>In-Person Swim Meet</strong> means all participating clubs compete together at the same pool. Use this when one host team is running the meet on site and all swimmers are swimming in a shared venue.
        </p>
        <p>
          <strong>Virtual Swim Meet</strong> means each club can compete from its own pool while Swimmingly combines the results into one meet. Use this when teams are not all swimming in the same place, but still want one shared meet format and one set of results.
        </p>
        <p>
          Choose the meet type based on how the meet will actually be hosted. In-person meets require a host pool and home team. Virtual meets focus on inviting clubs and setting the shared results course each team will swim into.
        </p>
      </Modal>

      {!isVirtual ? (
        <PoolSelectionModal
          visible={poolPickerVisible}
          title="Select Meet Pool"
          clubs={selectablePoolClubs}
          pools={createMeetSelectablePools as IPoolSelectionOption[]}
          genericPools={genericPoolOptions as IPoolSelectionOption[]}
          selectedClubId={poolPickerClubId}
          selectedPoolId={
            poolPickerTargetSessionId
              ? (sessionPools[poolPickerTargetSessionId]?.poolId ?? null)
              : (pool?.poolId ?? null)
          }
          allowedPoolCreateClubId={actingClubIdForPoolCreate}
          poolCreateBlockedReason={poolCreateBlockedReason}
          onChangeClub={(clubId) => {
            setPoolPickerClubId(clubId)
          }}
          onSelectPool={(selectedPool) => {
            if (poolPickerTargetSessionId) {
              setSessionPools((prev) => ({ ...prev, [poolPickerTargetSessionId]: selectedPool as IMeetPool }))
            } else {
              setPool(selectedPool as IMeetPool)
            }
            setPoolPickerTargetSessionId(null)
            setPoolPickerVisible(false)
          }}
          onCreatePoolForSelectedClub={() => {
            setCreatePoolModalVisible(true)
          }}
          onCancel={closePoolPicker}
        />
      ) : null}
      <CreateMeetPoolModal
        visible={createPoolModalVisible}
        targetClubId={actingClubIdForPoolCreate || 0}
        clubName={
          selectablePoolClubs.find((club) => club.clubId === actingClubIdForPoolCreate)
            ?.clubName || "Your Club"
        }
        onCancel={() => setCreatePoolModalVisible(false)}
        onCreated={async ({ poolId }) => {
          const nextPools = await loadSeasonPools()
          const createdPool = nextPools.find((poolOption) => poolOption.poolId === poolId) || null

          if (createdPool) {
            if (poolPickerTargetSessionId) {
              setSessionPools((prev) => ({ ...prev, [poolPickerTargetSessionId]: createdPool }))
              setPoolPickerClubId(createdPool.clubId)
            } else {
              setPool(createdPool)
              setPoolPickerClubId(createdPool.clubId)
            }
          }

          setCreatePoolModalVisible(false)
        }}
      />
      {isVirtual && season?.seasonId ? (
        <VirtualClubsManagerModal
          visible={virtualClubsModalVisible}
          clubSeasonsInMeet={virtualDraftClubSeasonsInMeet}
          availableClubs={availableClubsToAddToMeet}
          leagueClubs={virtualLeagueClubs}
          organizationGroups={virtualOrganizationGroups}
          onCancel={closeVirtualClubsManager}
          onSave={saveVirtualClubsManager}
          onAddClub={addVirtualDraftClub}
          onSwapClub={swapVirtualDraftClub}
          onRemoveClub={removeVirtualDraftClub}
        />
      ) : null}
    </Fragment>
    ) : (
      <div className="review-step-shell">
        <div className="review-step-hero">
          <div className="review-step-hero-copy">
            <Typography.Title level={3} style={{ marginBottom: 8 }}>
              {meetTypeChoice === "multiSession"
                ? (championshipName.trim() || "New Championship")
                : (meetName || "New Swim Meet")}
            </Typography.Title>
            <Typography.Paragraph style={{ marginBottom: 12 }}>
              Review your meet setup below & confirm to create your swim meet.
            </Typography.Paragraph>
            <Space size={[8, 8]} wrap>
              <Tag color={isVirtual ? "geekblue" : "blue"}>
                {isVirtual ? "Virtual Swim Meet" : "In-Person Swim Meet"}
              </Tag>
              {meetTypeChoice === "multiSession" && <Tag color="purple">Multi-Session</Tag>}
              {eventOrder?.name ? <Tag color="gold">{eventOrder.name}</Tag> : null}
            </Space>
          </div>
        </div>
        <div className="review-step-grid">
          <Card
            className={`review-step-card${reviewSectionsConfirmed.meetDetails ? " is-confirmed" : ""}`}
            title="Meet Details"
            extra={
              <Space size={8} align="center" wrap>
                {reviewSectionsConfirmed.meetDetails ? (
                  <Tag color="success" className="review-section-check-tag">Reviewed</Tag>
                ) : null}
                <Checkbox
                  className="review-section-check"
                  checked={reviewSectionsConfirmed.meetDetails}
                  onChange={(e) => setReviewSectionConfirmed("meetDetails", e.target.checked)}
                >
                  I reviewed the meet details
                </Checkbox>
              </Space>
            }
          >
            <Listy
              className="review-step-list"
              items={meetSummaryItems}
              rowKey="label"
              itemRender={(item) => (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                  <span className="review-step-list-label">{item.label}</span>
                  <span className="review-step-list-value">{item.value}</span>
                </div>
              )}
            />
          </Card>
          {meetTypeChoice === "multiSession" && (
            <Card
              className={`review-step-card${reviewSectionsConfirmed.championshipConfig ? " is-confirmed" : ""}`}
              title="Multi-Session Meet Configuration"
              extra={
                <Space size={8} align="center" wrap>
                  {reviewSectionsConfirmed.championshipConfig ? (
                    <Tag color="success" className="review-section-check-tag">Reviewed</Tag>
                  ) : null}
                  <Checkbox
                    className="review-section-check"
                    checked={reviewSectionsConfirmed.championshipConfig}
                    onChange={(e) => setReviewSectionConfirmed("championshipConfig", e.target.checked)}
                  >
                    I reviewed the multi-session meet setup
                  </Checkbox>
                </Space>
              }
            >
              <Listy
                className="review-step-list"
                rowKey="key"
                itemRender={(row) => row.render()}
                items={[
                  {
                    key: "series-name",
                    render: () => (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                        <span className="review-step-list-label">Meet Series Name</span>
                        <span className="review-step-list-value">{championshipName.trim() || "—"}</span>
                      </div>
                    ),
                  },
                  ...(samePoolAllSessions
                    ? [
                        {
                          key: "pool",
                          render: () => (
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                              <span className="review-step-list-label">Pool</span>
                              <span className="review-step-list-value">
                                {pool ? (
                                  <div>
                                    <div>{pool.poolName}</div>
                                    {pool.address && <div style={{ fontWeight: 400, color: "#777", fontSize: 12 }}>{pool.address}</div>}
                                  </div>
                                ) : (
                                  <Typography.Text type="danger">No pool selected</Typography.Text>
                                )}
                              </span>
                            </div>
                          ),
                        },
                      ]
                    : []),
                  ...sessions.map((s, idx) => {
                    const typeLabel = s.type === "timedFinals" ? "Timed Finals" : s.type === "prelims" ? "Prelims" : "Finals"
                    const SESSION_REVIEW_COLORS = ["#1677ff", "#52c41a", "#fa8c16", "#722ed1", "#eb2f96", "#13c2c2"]
                    const sessionColor = SESSION_REVIEW_COLORS[idx % SESSION_REVIEW_COLORS.length]
                    const assignedIds = sessionEventAssignments[s.id] || []
                    const assignedEvents = eventOrderPreviewEvents.filter((e) => assignedIds.includes(e.swimEventId))
                    return {
                      key: `session-${s.id}`,
                      render: () => (
                        <div
                          style={{
                            display: "block",
                            width: "100%",
                            padding: "10px 10px 10px 12px",
                            borderLeft: `3px solid ${sessionColor}`,
                            background: idx % 2 === 0 ? "#fff" : "#fafbff",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                            <span className="review-step-list-label">Session {idx + 1} – {typeLabel}</span>
                            <span className="review-step-list-value">
                              {s.date ? s.date.format("MMM D, YYYY") : "No date"}
                              {s.time ? ` · ${s.time.format("h:mma")}` : ""}
                            </span>
                          </div>
                          {!samePoolAllSessions && (
                            <div style={{ marginTop: 2, fontSize: 12 }}>
                              {(() => {
                                const sp = sessionPools[s.id]
                                if (!sp) return <Typography.Text type="danger" style={{ fontSize: 12 }}>No pool selected</Typography.Text>
                                return (
                                  <div style={{ color: "#777" }}>
                                    <div>{sp.poolName}</div>
                                    {sp.address && <div>{sp.address}</div>}
                                  </div>
                                )
                              })()}
                            </div>
                          )}
                          {assignedEvents.length > 0 ? (
                            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
                              {assignedEvents.map((e) => {
                                const num = e.displayNumber ?? e.eventNumber
                                const label = `${num} · ${e.distance} ${e.stroke}${e.isRelay ? " Relay" : ""} – ${e.gender} ${e.ageGroup}`
                                return (
                                  <div
                                    key={e.swimEventId}
                                    style={{ fontSize: 12, color: "#555", lineHeight: "1.7", display: "flex", alignItems: "center", gap: 6 }}
                                  >
                                    <span
                                      style={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        background: sessionColor,
                                        flexShrink: 0,
                                        display: "inline-block",
                                      }}
                                    />
                                    {label}
                                  </div>
                                )
                              })}
                            </div>
                          ) : null}
                        </div>
                      ),
                    }
                  }),
                  ...(sessions.some((s) => s.type === "prelims")
                    ? [
                        {
                          key: "finals-heats",
                          render: () => (
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                              <span className="review-step-list-label">Finals Heats</span>
                              <span className="review-step-list-value">
                                {["A Final",
                                  finalsConfig.bFinalEnabled ? "B Final" : null,
                                  finalsConfig.cFinalEnabled ? "C Final" : null,
                                  finalsConfig.dFinalEnabled ? "D Final" : null,
                                ].filter(Boolean).join(", ")}
                              </span>
                            </div>
                          ),
                        },
                        {
                          key: "finals-seeding",
                          render: () => (
                            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                              <span className="review-step-list-label">Finals Seeding</span>
                              <span className="review-step-list-value">
                                {finalsConfig.direction === "fastestFirst" ? "Fastest First (Championship)" : "Slowest First (Traditional)"}
                              </span>
                            </div>
                          ),
                        },
                      ]
                    : []),
                  {
                    key: "team-divisions",
                    render: () => (
                      <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                        <span className="review-step-list-label">Team Divisions</span>
                        <span className="review-step-list-value">
                          {enableDivisions && divisions.length > 0 ? divisions.join(", ") : "None"}
                        </span>
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          )}
          {meetTypeChoice !== "multiSession" && (
          <Card
            className={`review-step-card${reviewSectionsConfirmed.eventOrder ? " is-confirmed" : ""}`}
            title="Event Order Review"
            extra={
              <Space size={8} align="center" wrap>
                {reviewSectionsConfirmed.eventOrder ? (
                  <Tag color="success" className="review-section-check-tag">Reviewed</Tag>
                ) : null}
                <Checkbox
                  className="review-section-check"
                  checked={reviewSectionsConfirmed.eventOrder}
                  disabled={!eventOrderPreviewOpened}
                  onChange={(e) => setReviewSectionConfirmed("eventOrder", e.target.checked)}
                >
                  I reviewed the event order
                </Checkbox>
              </Space>
            }
          >
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
              <div>
                <Typography.Text type="secondary">Selected event order</Typography.Text>
                <Typography.Title level={4} style={{ margin: "4px 0 0" }}>
                  {eventOrder?.name || "No event order selected"}
                </Typography.Title>
              </div>
              <Alert
                type={eventOrderPreviewOpened ? "success" : "warning"}
                showIcon
                title={
                  eventOrderPreviewOpened
                    ? "Event order preview reviewed"
                    : "Preview the selected event order before creating this meet"
                }
                description={
                  eventOrderPreviewOpened
                    ? "Opening the preview marked this event order as reviewed for this meet."
                    : "Use the preview button to confirm the exact event lineup that will be copied into this meet."
                }
              />
              <Button
                type="default"
                icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                onClick={openEventOrderPreview}
              >
                Open Event Order Preview
              </Button>
            </Space>
          </Card>
          )}
          <Card
            className={`review-step-card${reviewSectionsConfirmed.participatingClubs ? " is-confirmed" : ""}`}
            title="Participating Clubs"
            extra={
              <Space size={8} align="center" wrap>
                {reviewSectionsConfirmed.participatingClubs ? (
                  <Tag color="success" className="review-section-check-tag">Reviewed</Tag>
                ) : null}
                <Checkbox
                  className="review-section-check"
                  checked={reviewSectionsConfirmed.participatingClubs}
                  onChange={(e) => setReviewSectionConfirmed("participatingClubs", e.target.checked)}
                >
                  I reviewed the participating clubs
                </Checkbox>
              </Space>
            }
          >
            <Space orientation="vertical" size={12} style={{ width: "100%" }}>
              {!isVirtual && inPersonReviewSeasonLabel ? (
                <Typography.Text type="secondary">
                  Season: {inPersonReviewSeasonLabel}
                </Typography.Text>
              ) : null}
              {selectedClubSummaries.length === 0 ? (
                <Empty description="No participating clubs selected" />
              ) : (
                <Listy
                  rowKey="key"
                  items={selectedClubSummaries}
                  itemRender={(club) => (
                    <div style={{ padding: "8px 0" }}>
                      <Space size={8} wrap>
                        <span>{club.clubName}</span>
                        {club.isHomeClub && !isVirtual ? <Tag color="blue">Host Club</Tag> : null}
                      </Space>
                      {isVirtual && club.seasonName ? (
                        <div style={{ color: "rgba(0, 0, 0, 0.45)" }}>{club.seasonName}</div>
                      ) : null}
                    </div>
                  )}
                />
              )}
            </Space>
          </Card>
          {meetTypeChoice !== "multiSession" && (
          <Card
            className={`review-step-card${reviewSectionsConfirmed.entryLimits ? " is-confirmed" : ""}`}
            title="Entry Limits & Scoring"
            extra={
              <Space size={8} align="center" wrap>
                {reviewSectionsConfirmed.entryLimits ? (
                  <Tag color="success" className="review-section-check-tag">Reviewed</Tag>
                ) : null}
                <Checkbox
                  className="review-section-check"
                  checked={reviewSectionsConfirmed.entryLimits}
                  onChange={(e) => setReviewSectionConfirmed("entryLimits", e.target.checked)}
                >
                  I reviewed the entry limits and scoring
                </Checkbox>
              </Space>
            }
          >
            <Listy
              className="review-step-list"
              items={entryAndScoringItems}
              rowKey="label"
              itemRender={(item) => (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
                  <span className="review-step-list-label">{item.label}</span>
                  <span className={`review-step-list-value${item.subtle ? " subtle" : ""}`}>
                    {item.value}
                  </span>
                </div>
              )}
            />
          </Card>
          )}
        </div>
      </div>
    )}
    </ShellComponent>
    <Modal
      open={meetEntriesDeadlineInfoVisible}
      title="Meet Entries Deadline"
      onCancel={() => setMeetEntriesDeadlineInfoVisible(false)}
      footer={[
        <Button key="close" type="primary" onClick={() => setMeetEntriesDeadlineInfoVisible(false)}>
          Close
        </Button>,
      ]}
    >
      <p>
        The meet entries deadline is the time at which entries must be completed. After the deadline, each club can be moved to Heat Sheet Ready so the meet host can merge the heat sheet.
      </p>
      <p>
        Only a user from the club who created the meet or a league admin can set the meet entries deadline.
      </p>
      <p>
        If automatic completion is enabled, post-deadline every club will automatically be set to Heat Sheet Ready. If set to manual, the meet host or any league admin can manually set each club to Heat Sheet Ready.
      </p>
      <p style={{ marginBottom: 0 }}>
        We recommend setting the meet entries deadline 24 hours prior to the meet start time. The Swimmingly app, as always, flexibly accommodates last minute entry changes the day of the meet.
      </p>
    </Modal>
    <Modal
      open={eventOrderPreviewVisible}
      onCancel={() => setEventOrderPreviewVisible(false)}
      footer={null}
      title={eventOrderPreviewName ? `Event Order: ${eventOrderPreviewName}` : "Event Order Preview"}
      width={720}
      destroyOnHidden
    >
      {eventOrderPreviewLoading ? (
        <p style={{ fontSize: 12, margin: 0 }}>Loading event order…</p>
      ) : eventOrderPreviewEvents.length > 0 ? (
        <>
          {eventOrderSessionNumbers.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 10, fontSize: 12 }}
              title={`Events split across ${eventOrderSessionNumbers.length} session${eventOrderSessionNumbers.length > 1 ? "s" : ""} (${eventOrderSessionNumbers.join(", ")}). Each session will receive its assigned events plus any shared (unassigned) events.`}
            />
          )}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12,
          }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Event #</th>
              {eventOrderPreviewEvents.some((e) => !!e.division) && (
                <th style={{ textAlign: "left", padding: "4px 6px" }}>Division</th>
              )}
              {eventOrderPreviewEvents.some((e) => e.sessionNumber != null) && (
                <th style={{ textAlign: "left", padding: "4px 6px" }}>Session</th>
              )}
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Age Group</th>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Gender</th>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Distance</th>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Stroke</th>
            </tr>
          </thead>
          <tbody>
            {eventOrderPreviewEvents.map((evt) => (
              <tr key={evt.swimEventId}>
                <td style={{ padding: "4px 6px" }}>
                  {resolveEventNumber(evt.displayNumber, evt.eventNumber)}
                </td>
                {eventOrderPreviewEvents.some((e) => !!e.division) && (
                  <td style={{ padding: "4px 6px" }}>{evt.division || "—"}</td>
                )}
                {eventOrderPreviewEvents.some((e) => e.sessionNumber != null) && (
                  <td style={{ padding: "4px 6px" }}>{evt.sessionNumber ?? "All"}</td>
                )}
                <td style={{ padding: "4px 6px" }}>{evt.ageGroup}</td>
                <td style={{ padding: "4px 6px" }}>{evt.gender}</td>
                <td style={{ padding: "4px 6px" }}>{evt.distance}</td>
                <td style={{ padding: "4px 6px" }}>{evt.stroke}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      ) : (
        <p style={{ fontSize: 12, margin: 0 }}>
          No event order is configured for this selection.
        </p>
      )}
    </Modal>
    <VirtualMeetInvitationResponseModal
      visible={organizerInvitationModalVisible}
      setVisible={(newVisible) => {
        setOrganizerInvitationModalVisible(newVisible)
        if (!newVisible) {
          setOrganizerInvitationIdToRespond(null)
          setOrganizerVirtualMeetIdToRespond(null)
          setOrganizerInvitationSeasonId(null)
        }
      }}
      virtualMeetInvitationId={organizerInvitationIdToRespond}
      virtualMeetId={organizerVirtualMeetIdToRespond}
      initialOrganizerClubId={impersonateClub?.clubId || null}
      clubId={impersonateClub?.clubId || null}
      initialSeasonId={organizerInvitationSeasonId}
      title="Complete Your Virtual Meet Setup"
      description="Your virtual meet is created. Next, choose the roster and pool your club will use to confirm your leg of the virtual meet."
      allowDecline={false}
      onSuccess={() => {
        refreshSchedule?.()
        setOrganizerInvitationIdToRespond(null)
        setOrganizerVirtualMeetIdToRespond(null)
        setOrganizerInvitationSeasonId(null)
      }}
    />

    <ImportEventOrderModal
      open={importEventOrderModalVisible}
      onClose={() => setImportEventOrderModalVisible(false)}
      actingUser={actingUser}
      availableClubsInSeason={availableClubsToAddToMeet}
      leagueIdForContext={availableClubsToAddToMeet.find((c) => c.leagueId)?.leagueId}
      onSuccess={({ eventOrderId, name, clubId, leagueId, entryLimits }) => {
        const newOrder = { eventOrderId, name }
        if (leagueId) {
          setAvailableLeagueEventOrders((prev) => [...prev, newOrder])
        } else {
          setAvailableClubEventOrders((prev) => [...prev, newOrder])
        }
        setAvailableEventOrders((prev) => [...prev, newOrder])
        setEventOrder(newOrder)
        setImportEventOrderModalVisible(false)

        const hasAnyLimit =
          entryLimits &&
          (entryLimits.individual != null ||
            entryLimits.relay != null ||
            entryLimits.total != null)
        if (hasAnyLimit) {
          const indLabel = entryLimits!.individual != null ? String(entryLimits!.individual) : "none"
          const relLabel = entryLimits!.relay != null ? String(entryLimits!.relay) : "none"
          const totLabel = entryLimits!.total != null ? String(entryLimits!.total) : "none"
          Modal.confirm({
            title: "Apply entry limits from event file?",
            content: `This file includes entry limits (Individual: ${indLabel}, Relay: ${relLabel}, Total: ${totLabel}). Apply them to this meet?`,
            okText: "Apply",
            cancelText: "Skip",
            onOk: () => {
              if (entryLimits!.individual != null) {
                setHasIndividualEntryLimit(true)
                setIndividualEntryLimit(entryLimits!.individual)
              }
              if (entryLimits!.relay != null) {
                setHasRelayEntryLimit(true)
                setRelayEntryLimit(entryLimits!.relay)
              }
              if (entryLimits!.total != null) {
                setHasTotalEntryLimit(true)
                setTotalEntryLimit(entryLimits!.total)
              }
            },
          })
        }
      }}
    />
    </Fragment>
  )
}
